// src/modulos/Desenvolvimento/ProjectStatusPage.js
// VERSION: 3.1 - With Notifications Integration

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { db } from '../../firebaseConfig';
import { doc, getDoc, collection, getDocs, addDoc, query, where } from 'firebase/firestore';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import { useActivityLogger } from '../../hooks/useActivityLogger';
import useAuth from '../../hooks/useAuth';  // ✅ ADDED
import styles from './Project.module.css';
import { exportProcessoManipulacao } from '../../documents';
import { countryOptions } from '../../data/countries';
import * as XLSX from 'xlsx';

// Notification imports
import { useNotifications } from '../../contexts/NotificationContext';
import { getModuleConfig } from '../../config/notificationConfig';

// React Icons
import {
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiArrowLeft,
    FiArrowRight,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiFileText,
    FiDownload,
    FiSearch,
    FiPackage,
    FiClipboard,
    FiCalendar,
    FiTag,
    FiMapPin,
    FiList,
    FiEye,
    FiPlus,
    FiUpload,
    FiPrinter,
    FiChevronUp,
    FiChevronDown,
    FiClock,
    FiUser,
    FiBox,
    FiDroplet,
    FiFolder,
    FiDatabase
} from 'react-icons/fi';
import { MdEdit, MdDelete, MdAdd, MdSave, MdCancel } from 'react-icons/md';

// ============================================
// 1. CONSTANTES E DEFINIÇÕES
// ============================================
const PROJECTS_COLLECTION = 'development-projects';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const PRODUCTS_COLLECTION = 'products';
const PRODUCT_TYPE_COLLECTION_PATH = 'parameters/types/tipo-produto';
const CATEGORY_COLLECTION_PATH = 'parameters/data/product-categories';
const CLIENTS_COLLECTION = 'clients';

const ITEMS_PER_PAGE = 10;

const SITUATION_OPTIONS = ['Active', 'On Hold', 'Completed', 'Cancelled'];

// Definição dos passos do projeto
const PROJECT_STEPS = [
    { id: 'formula', label: 'Fórmula', description: 'Elaboração da fórmula do produto', icon: '🧪' },
    { id: 'manipulation', label: 'Manipulação', description: 'Preparação e manipulação da amostra', icon: '🔬' },
    { id: 'quality', label: 'Controle de Qualidade', description: 'Análise e testes de qualidade', icon: '✓' },
    { id: 'client', label: 'Envio para o Cliente', description: 'Envio da amostra para aprovação', icon: '📦' },
    { id: 'status', label: 'Status da Amostra', description: 'Aprovação ou rejeição da amostra', icon: '🏷️' },
];

// Análises padrão para Controle de Qualidade
const DEFAULT_QUALITY_ANALYSES = [
    { id: 'cor', label: 'Cor', unit: '' },
    { id: 'odor', label: 'Odor', unit: '' },
    { id: 'ph', label: 'pH', unit: '' },
    { id: 'viscosidade', label: 'Viscosidade', unit: '' },
    { id: 'centrifuga', label: 'Centrífuga', unit: '' },
];

// ============================================
// 2. COMPONENTES AUXILIARES
// ============================================

// Toast de notificação
const Toast = ({ message, type, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(onClose, 5000);
        return () => clearTimeout(timer);
    }, [onClose]);

    const getIcon = () => {
        if (type === 'Success') return <FiCheck size={18} />;
        if (type === 'Error') return <FiAlertCircle size={18} />;
        return <FiInfo size={18} />;
    };

    return (
        <div className={`${styles.toast} ${styles[`toast${type}`]}`}>
            <span className={styles.toastIcon}>{getIcon()}</span>
            {message}
            <button onClick={onClose} className={styles.toastClose}>×</button>
        </div>
    );
};

// Status Badge Component
const StatusBadge = ({ status, t }) => {
    const getStatusStyle = () => {
        const activeLabel = t('pages.development.situation.active');
        const onHoldLabel = t('pages.development.situation.onHold');
        const completedLabel = t('pages.development.situation.completed');
        const cancelledLabel = t('pages.development.situation.cancelled');

        switch (status) {
            case activeLabel:
                return { background: '#d4edda', color: '#155724', borderLeft: '3px solid #28a745' };
            case onHoldLabel:
                return { background: '#fff3cd', color: '#856404', borderLeft: '3px solid #ffc107' };
            case completedLabel:
                return { background: '#d1ecf1', color: '#0c5460', borderLeft: '3px solid #17a2b8' };
            case cancelledLabel:
                return { background: '#f8d7da', color: '#721c24', borderLeft: '3px solid #dc3545' };
            default:
                return { background: '#e9ecef', color: '#6c757d', borderLeft: '3px solid #6c757d' };
        }
    };

    return (
        <span className={styles.statusBadge} style={getStatusStyle()}>
            {status}
        </span>
    );
};

// Componente de Passos do Projeto - Versão Modernizada
const ProjectSteps = ({ currentStep, onStepChange, project, t }) => {
    const getStepStatus = (stepId) => {
        if (!project) return 'pending';
        const stepData = project.steps?.[stepId];
        if (!stepData) return 'pending';
        return stepData.status || 'pending';
    };

    const isStepComplete = (stepId) => {
        const status = getStepStatus(stepId);
        return status === 'completed' || status === 'approved' || status === 'product_registered';
    };

    const isStepActive = (stepId) => {
        return currentStep === stepId;
    };

    const isStepBlocked = (stepId) => {
        if (stepId === 'formula') return false;
        const prevSteps = PROJECT_STEPS.filter(s => {
            const idx = PROJECT_STEPS.findIndex(st => st.id === stepId);
            return PROJECT_STEPS.indexOf(s) < idx;
        });
        return prevSteps.some(s => getStepStatus(s.id) !== 'completed' && getStepStatus(s.id) !== 'approved' && getStepStatus(s.id) !== 'product_registered');
    };

    // Get translated label for step
    const getStepLabel = (stepId) => {
        const labelMap = {
            'formula': t('pages.development.projectSteps.formula'),
            'manipulation': t('pages.development.projectSteps.manipulation'),
            'quality': t('pages.development.projectSteps.quality'),
            'client': t('pages.development.projectSteps.client'),
            'status': t('pages.development.projectSteps.status')
        };
        return labelMap[stepId] || stepId;
    };

    // Get translated status text
    const getStatusText = (status, isActive, isBlocked) => {
        if (isActive) return t('pages.development.projectSteps.inProgress');
        if (status === 'completed' || status === 'approved' || status === 'product_registered') {
            return t('pages.development.projectSteps.completed');
        }
        if (isBlocked) return t('pages.development.projectSteps.blocked');
        return t('pages.development.projectSteps.pending');
    };

    return (
        <div className={styles.stepsContainer}>
            {PROJECT_STEPS.map((step, index) => {
                const status = getStepStatus(step.id);
                const isComplete = isStepComplete(step.id);
                const isActive = isStepActive(step.id);
                const isBlocked = isStepBlocked(step.id);
                const isPending = status === 'pending' && !isBlocked;

                let stepClass = styles.stepItem;
                if (isActive) stepClass += ` ${styles.stepActive}`;
                if (isComplete) stepClass += ` ${styles.stepComplete}`;
                if (isPending) stepClass += ` ${styles.stepPending}`;
                if (isBlocked) stepClass += ` ${styles.stepBlocked}`;

                return (
                    <React.Fragment key={step.id}>
                        <div
                            className={stepClass}
                            onClick={() => !isBlocked && onStepChange(step.id)}
                            style={{ cursor: isBlocked ? 'not-allowed' : 'pointer' }}
                            title={isBlocked ? t('pages.projectStatus.projectSteps.blockedTooltip') : step.description}
                        >
                            <div className={styles.stepIconWrapper}>
                                <span className={styles.stepIcon}>{step.icon}</span>
                                {isComplete && <span className={styles.stepCheckmark}><FiCheck size={10} /></span>}
                            </div>
                            <div className={styles.stepContent}>
                                <span className={styles.stepLabel}>{getStepLabel(step.id)}</span>
                                <span className={styles.stepStatus}>
                                    {getStatusText(status, isActive, isBlocked)}
                                </span>
                            </div>
                        </div>
                        {index < PROJECT_STEPS.length - 1 && (
                            <div className={`${styles.stepConnector} ${isComplete ? styles.connectorComplete : ''}`} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

// ============================================
// 3. COMPONENTE PRINCIPAL
// ============================================
const ProjectStatusPage = () => {
    const { t } = useTranslation();

    // --- Permission Hook ---
    const can = usePagePermissions('acompanhamento_de_projeto');

    const { logActivity } = useActivityLogger();
    const { addNotification } = useNotifications();
    const { currentUser } = useAuth();  // ✅ ADDED

    // --- Estados do Modal ---
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [toasts, setToasts] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentStep, setCurrentStep] = useState('formula');
    const [productCreated, setProductCreated] = useState(false);

    // --- Estados de Paginação e Filtro ---
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterSituation, setFilterSituation] = useState('');
    const [filterStepStatus, setFilterStepStatus] = useState('');

    // --- Estados do Formulário - Formula Elaboration ---
    const [rawMaterialsList, setRawMaterialsList] = useState([]);
    const [qualityCriteria, setQualityCriteria] = useState([]);
    const [preparationSteps, setPreparationSteps] = useState([]);
    const [currentStepText, setCurrentStepText] = useState('');
    const [markup, setMarkup] = useState('1.00');

    // --- Estados para Análises de Qualidade ---
    const [qualityAnalyses, setQualityAnalyses] = useState(() => {
        return DEFAULT_QUALITY_ANALYSES.map(analysis => ({
            ...analysis,
            value: '',
            observations: '',
        }));
    });

    // --- Estados para Importação de MPs ---
    const [showImportMPModal, setShowImportMPModal] = useState(false);
    const [importSearchTerm, setImportSearchTerm] = useState('');
    const [filterProductTypeId, setFilterProductTypeId] = useState('');
    const [filterCategoryId, setFilterCategoryId] = useState('');

    // --- Estados para o Modal de Registro de Produto ---
    const [showProductRegistrationModal, setShowProductRegistrationModal] = useState(false);
    const [productData, setProductData] = useState(null);
    const [isCreatingProduct, setIsCreatingProduct] = useState(false);

    // --- Estados para o Modal de Manipulação ---
    const [showManipulationModal, setShowManipulationModal] = useState(false);
    const [manipulationData, setManipulationData] = useState({
        calculadaKgl: '',
        perda: '',
        programadaKgl: ''
    });
    const [selectedProjectForManipulation, setSelectedProjectForManipulation] = useState(null);

    // --- Hooks de Dados ---
    const { data: projects, loading: mainLoading, updateDocument, refreshData: refreshProjects } = useFirestore(PROJECTS_COLLECTION, 'code');

    // --- Dados Auxiliares ---
    const [allRawMaterials, setAllRawMaterials] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [allProductTypes, setAllProductTypes] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [allQualityCriteria, setAllQualityCriteria] = useState([]);
    const [preparationSetups, setPreparationSetups] = useState([]);
    const [auxiliaryDataLoading, setAuxiliaryDataLoading] = useState(true);
    const [selectedSetup, setSelectedSetup] = useState('');

    // Referência para armazenar dados originais
    const originalItemDataRef = useRef(null);
    const dataFetchedRef = useRef(false);

    // --- Funções Auxiliares ---
    const addToast = useCallback((message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    }, []);

    // ============================================
    // 3.1 NOTIFICATION FUNCTIONS
    // ============================================

    /**
     * Notifica usuários sobre mudanças no status do projeto
     */
    const notifyProjectStatusChange = useCallback(async (project, stepId, status, actionType) => {
        try {
            // Buscar usuários com permissão de acompanhamento de projeto
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const targetUsers = [];

            usersSnapshot.forEach(doc => {
                const userData = doc.data();
                const roles = Array.isArray(userData.role) ? userData.role : [userData.role];
                const permissions = Array.isArray(userData.permissions) ? userData.permissions : [];

                if (roles.some(r => r === 'Diretoria' || r === 'Diretoria (Admin)' || r === 'Gerente de Projetos') ||
                    permissions.includes('acompanhamento_de_projeto')) {
                    targetUsers.push({
                        uid: doc.id,
                        ...userData
                    });
                }
            });

            // Also notify the project creator
            let creatorUser = null;
            if (project.createdBy) {
                usersSnapshot.forEach(doc => {
                    const userData = doc.data();
                    if (userData.nome === project.createdBy || userData.email === project.createdBy) {
                        creatorUser = { uid: doc.id, ...userData };
                    }
                });
            }

            // Add creator to target users if not already included
            if (creatorUser && !targetUsers.some(u => u.uid === creatorUser.uid)) {
                targetUsers.push(creatorUser);
            }

            console.log(`📢 Enviando notificações de status de projeto para ${targetUsers.length} usuários`);

            const config = getModuleConfig('acompanhamento_de_projeto');

            // Get step label
            const stepLabels = {
                'formula': t('pages.development.projectSteps.formula'),
                'manipulation': t('pages.development.projectSteps.manipulation'),
                'quality': t('pages.development.projectSteps.quality'),
                'client': t('pages.development.projectSteps.client'),
                'status': t('pages.development.projectSteps.status')
            };
            const stepLabel = stepLabels[stepId] || stepId;

            // Get status label
            const statusLabels = {
                'completed': 'Concluído',
                'approved': 'Aprovado',
                'rejected': 'Rejeitado',
                'sent': 'Enviado',
                'product_registered': 'Produto Registrado'
            };
            const statusLabel = statusLabels[status] || status;

            let title, message, priority, type;
            if (status === 'completed') {
                title = `✅ Passo "${stepLabel}" Concluído`;
                message = `O passo "${stepLabel}" do projeto ${project.code} - ${project.projectName} foi concluído`;
                priority = 'Média';
                type = 'project_step_completed';
            } else if (status === 'approved') {
                title = `✅ Amostra Aprovada`;
                message = `A amostra do projeto ${project.code} - ${project.projectName} foi aprovada pelo cliente`;
                priority = 'Alta';
                type = 'sample_approved';
            } else if (status === 'rejected') {
                title = `❌ Amostra Rejeitada`;
                message = `A amostra do projeto ${project.code} - ${project.projectName} foi rejeitada pelo cliente`;
                priority = 'Alta';
                type = 'sample_rejected';
            } else if (status === 'sent') {
                title = `📦 Amostra Enviada`;
                message = `A amostra do projeto ${project.code} - ${project.projectName} foi enviada para ${project.clientName || 'cliente'}`;
                priority = 'Média';
                type = 'sample_sent';
            } else if (status === 'product_registered') {
                title = `📦 Produto Registrado`;
                message = `O produto ${project.code} - ${project.projectName} foi registrado a partir do projeto de desenvolvimento`;
                priority = 'Alta';
                type = 'product_registered';
            } else {
                return;
            }

            const baseNotification = {
                type: type,
                module: 'acompanhamento_de_projeto',
                moduleIcon: config?.icon || '📊',
                moduleColor: config?.color || '#17a2b8',
                moduleName: config?.name || 'Acompanhamento de Projetos',
                title: title,
                fullMessage: `Detalhes:\n• Projeto: ${project.code} - ${project.projectName}\n• Cliente: ${project.clientName || 'N/A'}\n• Passo: ${stepLabel}\n• Status: ${statusLabel}\n• Atualizado por: ${currentUser?.nome || 'Sistema'}`,
                timestamp: new Date().toISOString(),
                read: false,
                priority: priority,
                route: `/projeto/acompanhamento-de-projeto`,
                data: {
                    projectId: project.id,
                    projectCode: project.code,
                    projectName: project.projectName,
                    clientName: project.clientName,
                    stepId: stepId,
                    stepLabel: stepLabel,
                    status: status,
                    statusLabel: statusLabel,
                    updatedBy: currentUser?.nome || 'Sistema'
                }
            };

            for (const user of targetUsers) {
                if (user.uid === currentUser?.uid) continue;

                const notification = {
                    ...baseNotification,
                    id: `project_status_${project.id}_${stepId}_${user.uid}_${Date.now()}`,
                    message: `${user.nome || 'Usuário'}, ${message}`,
                    userId: user.uid
                };

                addNotification(notification);

                try {
                    await addDoc(collection(db, 'notifications'), {
                        userId: user.uid,
                        ...notification,
                        createdAt: new Date()
                    });
                } catch (err) {
                    console.warn('Erro ao salvar notificação:', err);
                }
            }

            console.log(`✅ Notificações de status enviadas para ${targetUsers.length} usuários`);
        } catch (error) {
            console.error('❌ Erro ao enviar notificações de status:', error);
        }
    }, [currentUser, addNotification, t]);

    // --- Função para buscar dados auxiliares (apenas uma vez) ---
    const fetchAuxiliaryData = useCallback(async () => {
        if (dataFetchedRef.current) return;
        dataFetchedRef.current = true;

        setAuxiliaryDataLoading(true);
        try {
            const mpCollectionRef = collection(db, RAW_MATERIALS_COLLECTION);
            const mpSnapshot = await getDocs(mpCollectionRef);
            let fetchedRawMaterials = mpSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            const productsCollectionRef = collection(db, PRODUCTS_COLLECTION);
            const productsSnapshot = await getDocs(productsCollectionRef);
            let fetchedProducts = productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            const typesCollectionRef = collection(db, PRODUCT_TYPE_COLLECTION_PATH);
            const typesSnapshot = await getDocs(typesCollectionRef);
            let fetchedTypes = typesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedTypes.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

            const categoriesCollectionRef = collection(db, CATEGORY_COLLECTION_PATH);
            const categoriesSnapshot = await getDocs(categoriesCollectionRef);
            let fetchedCategories = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedCategories.sort((a, b) => (a.order || 0) - (b.order || 0));

            const criteriaCollectionRef = collection(db, 'parameters/data/criterio-qualidade');
            const criteriaSnapshot = await getDocs(criteriaCollectionRef);
            let fetchedCriteria = criteriaSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            const setupsCollectionRef = collection(db, 'parameters/production/setups-preparo');
            const setupsSnapshot = await getDocs(setupsCollectionRef);
            let fetchedSetups = setupsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedSetups.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

            setAllRawMaterials(fetchedRawMaterials);
            setAllProducts(fetchedProducts);
            setAllProductTypes(fetchedTypes);
            setAllCategories(fetchedCategories);
            setAllQualityCriteria(fetchedCriteria);
            setPreparationSetups(fetchedSetups);

        } catch (error) {
            console.error("Erro ao buscar dados auxiliares.", error);
            addToast(t('pages.projectStatus.error', { error: error.message }), 'Error');
        } finally {
            setAuxiliaryDataLoading(false);
        }
    }, [addToast, t]);

    useEffect(() => {
        fetchAuxiliaryData();
    }, [fetchAuxiliaryData]);

    // --- Função para obter o preço mais recente ---
    const getLatestPrice = useCallback((mpId, fallbackValue) => {
        const mpData = allRawMaterials.find(item => item.id === mpId);
        if (mpData && mpData.custoCompra !== undefined && mpData.custoCompra !== null) {
            return parseFloat(mpData.custoCompra) || 0;
        }
        const productData = allProducts.find(p => p.id === mpId);
        if (productData && productData.totalFormulationCost !== undefined) {
            return parseFloat(productData.totalFormulationCost) || 0;
        }
        if (mpData && mpData.suppliers && mpData.suppliers[0]) {
            return parseFloat(mpData.suppliers[0].valor) || 0;
        }
        return parseFloat(fallbackValue) || 0;
    }, [allRawMaterials, allProducts]);

    // --- Função para buscar dados do cliente ---
    const fetchClientData = useCallback(async (clientId) => {
        if (!clientId) return null;
        try {
            const clientRef = doc(db, CLIENTS_COLLECTION, clientId);
            const clientSnap = await getDoc(clientRef);
            if (clientSnap.exists()) {
                return { id: clientSnap.id, ...clientSnap.data() };
            }
            return null;
        } catch (error) {
            console.error('Error fetching client data:', error);
            return null;
        }
    }, []);

    // --- Função para gerar etiquetas de envio para todos os projetos ---
    const handlePrintAllShippingLabels = useCallback(async () => {
        if (!projects || projects.length === 0) {
            addToast(t('pages.projectStatus.shippingStep.noProjects'), 'Error');
            return;
        }

        try {
            const allLabelsData = [];
            let successCount = 0;
            let errorCount = 0;

            addToast(t('pages.projectStatus.shippingLabel.generatingAll'), 'Info');

            for (const project of projects) {
                try {
                    let clientCountryCode = '';
                    let clientName = project.clientName || 'N/A';

                    if (project.clientId) {
                        const clientData = await fetchClientData(project.clientId);
                        if (clientData) {
                            clientName = clientData.razaoSocial || clientData.nomeFantasia || clientName;
                            if (clientData.pais) {
                                clientCountryCode = clientData.pais;
                            }
                            if (!clientCountryCode && clientData.addresses && clientData.addresses.length > 0) {
                                let mainAddress = clientData.addresses.find(addr =>
                                    addr.tipo?.toUpperCase() === 'COMERCIAL' ||
                                    addr.tipo?.toUpperCase() === 'PRINCIPAL'
                                );
                                if (!mainAddress) {
                                    mainAddress = clientData.addresses[0];
                                }
                                if (mainAddress && mainAddress.pais) {
                                    clientCountryCode = mainAddress.pais;
                                }
                            }
                        }
                    }

                    if (!clientCountryCode) {
                        clientCountryCode = project.clientCountry || '';
                    }

                    let fullCountryName = clientCountryCode;
                    if (clientCountryCode) {
                        const countryOption = countryOptions.find(opt => opt.value === clientCountryCode);
                        if (countryOption) {
                            fullCountryName = countryOption.label;
                        }
                    }

                    const fullAddress = fullCountryName || 'N/A';

                    let manufactureDate = '';
                    if (project.steps?.manipulation?.updatedAt) {
                        const manipulationDate = new Date(project.steps.manipulation.updatedAt);
                        manufactureDate = manipulationDate.toLocaleDateString('pt-BR');
                    } else if (project.steps?.manipulation?.data?.concludedAt) {
                        const manipulationDate = new Date(project.steps.manipulation.data.concludedAt);
                        manufactureDate = manipulationDate.toLocaleDateString('pt-BR');
                    } else if (project.updatedAt) {
                        const updateDate = new Date(project.updatedAt);
                        manufactureDate = updateDate.toLocaleDateString('pt-BR');
                    } else {
                        manufactureDate = new Date().toLocaleDateString('pt-BR');
                    }

                    const parseDate = (dateStr) => {
                        if (dateStr.includes('/')) {
                            const parts = dateStr.split('/');
                            return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
                        }
                        const d = new Date(dateStr);
                        if (!isNaN(d.getTime())) {
                            return d;
                        }
                        return new Date();
                    };

                    const manufactureDateObj = parseDate(manufactureDate);
                    const expiryDateObj = new Date(manufactureDateObj);
                    expiryDateObj.setFullYear(expiryDateObj.getFullYear() + 3);
                    const expiryDateStr = expiryDateObj.toLocaleDateString('pt-BR');

                    allLabelsData.push({
                        'LOTE': project.code || 'N/A',
                        'PROJETO': project.projectName || 'N/A',
                        'CLIENTE': clientName,
                        'ENDERECO': fullAddress,
                        'FABRICACAO': manufactureDate,
                        'VALIDADE': expiryDateStr,
                    });
                    successCount++;
                } catch (error) {
                    console.error(`Error processing project ${project.code}:`, error);
                    errorCount++;
                }
            }

            if (allLabelsData.length === 0) {
                addToast(t('pages.projectStatus.shippingLabel.noValidProjects'), 'Error');
                return;
            }

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(allLabelsData);

            const colWidths = [
                { wch: 25 },
                { wch: 30 },
                { wch: 45 },
                { wch: 30 },
                { wch: 15 },
                { wch: 30 }
            ];
            ws['!cols'] = colWidths;

            XLSX.utils.book_append_sheet(wb, ws, 'Etiquetas');

            const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wbout], { type: 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const fileName = `etiquetas-todos-desenvolvimentos-${new Date().toISOString().split('T')[0]}.xlsx`;

            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            addToast(
                t('pages.projectStatus.shippingLabel.downloadAllSuccess', {
                    total: allLabelsData.length,
                    file: fileName
                }),
                'Success'
            );

            if (errorCount > 0) {
                addToast(
                    t('pages.projectStatus.shippingLabel.errorsCount', { errors: errorCount }),
                    'Warning'
                );
            }
            logActivity(
                'shipping_labels_generated',
                'development-projects/all',
                `Etiquetas de envio geradas para ${allLabelsData.length} projetos`
            );

        } catch (error) {
            console.error('Error generating all shipping labels:', error);
            addToast(`${t('pages.projectStatus.shippingLabel.generateAllError')}: ${error.message}`, 'Error');
        }
    }, [projects, addToast, t, fetchClientData]);

    // --- Opções de matérias-primas para o Select ---
    const rawMaterialOptions = useMemo(() => {
        if (!allRawMaterials || allRawMaterials.length === 0) return [];

        const embalagemTypeId = allProductTypes.find(t => t.name?.toLowerCase() === 'embalagem')?.id;
        const embalagemCategoryId = allCategories.find(c => c.name?.toLowerCase() === 'embalagem')?.id;
        const serigrafiaTypeId = allProductTypes.find(t => t.name?.toLowerCase() === 'serigrafia')?.id;
        const serigrafiaCategoryId = allCategories.find(c => c.name?.toLowerCase() === 'serigrafia')?.id;

        const semiAcabadoCategoryId = allCategories.find(c => c.name?.toUpperCase() === 'SEMI ACABADO')?.id;

        const filteredMPs = allRawMaterials.filter(mp => {
            const isMPCode = mp.code && typeof mp.code === 'string' && mp.code.toUpperCase().startsWith('MP');
            const isEmbalagem = (embalagemTypeId && mp.productTypeIds?.includes(embalagemTypeId)) ||
                (embalagemCategoryId && mp.categoryIds?.includes(embalagemCategoryId));
            const isSerigrafia = (serigrafiaTypeId && mp.productTypeIds?.includes(serigrafiaTypeId)) ||
                (serigrafiaCategoryId && mp.categoryIds?.includes(serigrafiaCategoryId));

            if (!isMPCode && !isEmbalagem && !isSerigrafia) return false;

            const matchesType = !filterProductTypeId || (mp.productTypeIds || []).includes(filterProductTypeId);
            const matchesCategory = !filterCategoryId || (mp.categoryIds || []).includes(filterCategoryId);

            return matchesType && matchesCategory;
        });

        const semiAcabados = (allProducts || []).filter(p => {
            const isSemi = semiAcabadoCategoryId && p.categoryIds?.includes(semiAcabadoCategoryId);
            if (!isSemi) return false;

            const matchesType = !filterProductTypeId || (p.productTypeIds || []).includes(filterProductTypeId);
            const matchesCategory = !filterCategoryId || (p.categoryIds || []).includes(filterCategoryId);

            return matchesType && matchesCategory;
        });

        const combinedOptions = [
            ...filteredMPs.map(mp => ({
                value: mp.id,
                label: `[MP] ${mp.code} - ${mp.commercialName || mp.inciName || 'N/A'}`,
                source: 'raw-materials'
            })),
            ...semiAcabados.map(p => ({
                value: p.id,
                label: `[SA] ${p.code} - ${p.commercialName}`,
                source: 'products'
            }))
        ];

        return combinedOptions.sort((a, b) => a.label.localeCompare(b.label));
    }, [allRawMaterials, allProducts, filterProductTypeId, filterCategoryId, allProductTypes, allCategories]);

    // --- Função para preparar dados do produto a partir do projeto ---
    const prepareProductDataFromProject = useCallback((project) => {
        if (!project) return null;

        const productTypeIds = project.productTypeIds || [];
        const categoryIds = project.categoryIds || [];

        const calculateTotalCost = () => {
            const unitCategoryIds = allCategories
                .filter(c => ['SERIGRAFIA', 'EMBALAGEM'].includes(c.name?.toUpperCase()))
                .map(c => c.id);

            const isUnitCalculation = (mpId) => {
                const mpData = allRawMaterials.find(item => item.id === mpId);
                return mpData?.categoryIds?.some(id => unitCategoryIds.includes(id));
            };

            const rawMaterialsCost = (project.rawMaterials || []).reduce((acc, mp) => {
                if (!isUnitCalculation(mp.id)) {
                    const price = getLatestPrice(mp.id, mp.valor);
                    const quantity = parseFloat(mp.quantity) || 0;
                    return acc + (price * quantity) / 100;
                }
                return acc;
            }, 0);

            const netWeightVal = parseFloat(project.netWeight) || 0;
            const rawMaterialsTotalCost = rawMaterialsCost * netWeightVal;

            const unitMaterialsCost = (project.rawMaterials || []).reduce((acc, mp) => {
                if (isUnitCalculation(mp.id)) {
                    const price = getLatestPrice(mp.id, mp.valor);
                    const quantity = parseFloat(mp.quantity) || 0;
                    return acc + (price * quantity);
                }
                return acc;
            }, 0);

            return rawMaterialsTotalCost + unitMaterialsCost;
        };

        const totalCost = calculateTotalCost();
        const markupValue = parseFloat(project.markup) || 1;

        return {
            sigla: project.sigla || '',
            code: project.code || '',
            commercialName: project.projectName || '',
            situation: 'Ativo',
            netWeight: project.netWeight || '',
            grossWeight: project.grossWeight || '',
            density: project.density || '',
            lossIndex: project.lossIndex || '',
            productTypeIds: productTypeIds,
            categoryIds: categoryIds,
            rawMaterials: project.rawMaterials || [],
            qualityCriteria: project.qualityCriteria || [],
            preparationSteps: project.preparationSteps || [],
            markup: project.markup || '1.00',
            totalFormulationCost: totalCost,
            technical: {
                gramatura: project.gramatura || '',
                gramaturaUnit: project.gramaturaUnit || 'g',
                shelfLifeMonths: project.shelfLifeMonths || '',
                productionPhases: project.productionPhases || [],
                boxTypeId: project.boxTypeId || '',
            },
            fiscal: {
                origemOperacaoComercial: 'Fabricação Própria',
                origemMercadoriaIcms: '0',
                ncmId: '',
                excecaoFiscalIpi: false,
                excecaoFiscalIva: false,
                excecaoFiscalPisCofins: false,
                valorPautaRs: 0,
                classificacaoTributariaRt: '',
                cfopSaidaEstadual: '',
                cfopSaidaEstadualComSt: '',
                cfopIndustrializacaoEstadual: '',
                cfopSaidaBrindeBonificacaoDoacaoEstadual: '',
                cfopSaidaBrindeBonificacaoDoacaoEstadualComSt: '',
                cfopSaidaNaoContribuinteInterestadual: '',
                cfopBonificacaoExportacao: '',
                baseCalculoSt: 0,
                ipiSaida: 0,
                cfopSaidaInterestadual: '',
                cfopSaidaBrindeBonificacaoDoacaoInterestadual: '',
                cfopSaidaNaoContribuinteInterestadualOutro: '',
                cfopBonificacaoExportacaoOutro: '',
                cfopExportacao: '',
            },
            suggestedPrice: totalCost * markupValue,
            fromProjectId: project.id,
            fromProjectCode: project.code,
            isFromProject: true,
        };
    }, [allCategories, allRawMaterials, getLatestPrice]);

    // --- Handlers para Matérias-Primas ---
    const handleAddRawMaterial = (selectedOption) => {
        if (!selectedOption) return;

        const itemId = selectedOption.value;
        const isSemiAcabado = selectedOption.source === 'products';

        let itemToAdd = isSemiAcabado
            ? allProducts.find(p => p.id === itemId)
            : allRawMaterials.find(mp => mp.id === itemId);

        if (itemToAdd) {
            const valorCusto = isSemiAcabado
                ? (itemToAdd.totalFormulationCost || 0)
                : ((itemToAdd.suppliers && itemToAdd.suppliers[0]) ? itemToAdd.suppliers[0].valor : 0);

            const commercialName = itemToAdd.commercialName || itemToAdd.inciName || 'N/A';

            const newComponent = {
                rowId: crypto.randomUUID(),
                id: itemToAdd.id,
                order: rawMaterialsList.length + 1,
                fase: '',
                inciName: itemToAdd.inciName || '',
                commercialName: commercialName,
                code: itemToAdd.code,
                valor: valorCusto,
                quantity: '',
                isSemiAcabado: isSemiAcabado,
                productTypeIds: itemToAdd.productTypeIds || [],
                categoryIds: itemToAdd.categoryIds || [],
            };
            setRawMaterialsList(prev => [...prev, newComponent]);
        }
    };

    const handleRemoveRawMaterial = (rowIdToRemove) => {
        const updatedList = rawMaterialsList
            .filter(mp => mp.rowId !== rowIdToRemove)
            .map((mp, index) => ({ ...mp, order: index + 1 }));
        setRawMaterialsList(updatedList);
    };

    const handleUpdateRawMaterialField = (rowId, field, value) => {
        setRawMaterialsList(prev => prev.map(mp =>
            mp.rowId === rowId ? { ...mp, [field]: value } : mp
        ));
    };

    const handleMoveRawMaterial = (index, direction) => {
        const list = [...rawMaterialsList];
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === list.length - 1)) return;
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        [list[index], list[newIndex]] = [list[newIndex], list[index]];
        list[index].order = index + 1;
        list[newIndex].order = newIndex + 1;
        setRawMaterialsList(list);
    };

    // --- Handlers para Critérios de Qualidade ---
    const handleAddCriteria = (e) => {
        const criteriaId = e.target.value;
        if (!criteriaId || qualityCriteria.find(c => c.id === criteriaId)) return;
        const criteriaToAdd = allQualityCriteria.find(c => c.id === criteriaId);
        if (criteriaToAdd) {
            const newCriteria = {
                id: criteriaToAdd.id,
                analysis: criteriaToAdd.analysis,
                method: criteriaToAdd.method,
                specification: criteriaToAdd.specification || '',
                control: 'Processo',
            };
            setQualityCriteria(prev => [...prev, newCriteria]);
            e.target.value = '';
        }
    };

    const handleRemoveCriteria = (idToRemove) => {
        const updatedCriteria = qualityCriteria.filter(c => c.id !== idToRemove);
        setQualityCriteria(updatedCriteria);
    };

    const handleUpdateCriteriaField = (id, field, value) => {
        setQualityCriteria(prev => prev.map(c =>
            c.id === id ? { ...c, [field]: value } : c
        ));
    };

    const handleMoveCriteria = (index, direction) => {
        const list = [...qualityCriteria];
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === list.length - 1)) return;
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        [list[index], list[newIndex]] = [list[newIndex], list[index]];
        setQualityCriteria(list);
    };

    // --- Handlers para Análises de Qualidade ---
    const handleUpdateQualityAnalysis = (analysisId, field, value) => {
        setQualityAnalyses(prev => prev.map(analysis =>
            analysis.id === analysisId ? { ...analysis, [field]: value } : analysis
        ));
    };

    // --- Handlers para Modo de Preparo ---
    const handleAddPreparationStep = () => {
        const trimmedStep = currentStepText.trim();
        if (!trimmedStep) return;

        const newStep = {
            id: crypto.randomUUID(),
            order: preparationSteps.length + 1,
            description: trimmedStep
        };

        setPreparationSteps(prev => [...prev, newStep]);
        setCurrentStepText('');
        setSelectedSetup('');
    };

    const handleUpdatePreparationStep = (id, value) => {
        setPreparationSteps(prev => prev.map(step =>
            step.id === id ? { ...step, description: value } : step
        ));
        setSelectedSetup('');
    };

    const handleRemovePreparationStep = (idToRemove) => {
        const updatedSteps = preparationSteps
            .filter(step => step.id !== idToRemove)
            .map((step, index) => ({ ...step, order: index + 1 }));
        setPreparationSteps(updatedSteps);
        setSelectedSetup('');
    };

    const handleMovePreparationStep = (index, direction) => {
        const list = [...preparationSteps];
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === list.length - 1)) return;
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        [list[index], list[newIndex]] = [list[newIndex], list[index]];

        const updatedList = list.map((step, i) => ({ ...step, order: i + 1 }));
        setPreparationSteps(updatedList);
        setSelectedSetup('');
    };

    const handleStepKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddPreparationStep();
        }
    };

    // --- Handler para carregar Setup de Preparo ---
    const handleLoadSetup = (setupId) => {
        if (!setupId) {
            setSelectedSetup('');
            return;
        }

        const setup = preparationSetups.find(s => s.id === setupId);
        if (setup && setup.preparationSteps) {
            const stepsWithIds = setup.preparationSteps.map(step => ({
                id: crypto.randomUUID(),
                order: step.order,
                description: step.description
            }));
            setPreparationSteps(stepsWithIds);
            setSelectedSetup(setupId);
        }
    };

    // --- Handler para importar MPs de outro produto ---
    const handleImportMPs = (sourceProduct) => {
        if (!sourceProduct.rawMaterials || sourceProduct.rawMaterials.length === 0) {
            setFormError(t('pages.projectStatus.importModal.noMaterials'));
            return;
        }

        const updatedMaterials = sourceProduct.rawMaterials.map((mp, index) => {
            const currentPrice = getLatestPrice(mp.id, mp.valor);
            return {
                ...mp,
                rowId: crypto.randomUUID(),
                valor: currentPrice,
                order: rawMaterialsList.length + index + 1,
                commercialName: mp.commercialName || mp.inciName || 'N/A',
            };
        });

        setRawMaterialsList(prev => [...prev, ...updatedMaterials]);
        setShowImportMPModal(false);
        setImportSearchTerm('');
        setFormSuccess(t('pages.projectStatus.importModal.importSuccess', { count: updatedMaterials.length }));

        logActivity(
            'raw_materials_imported',
            `development-project/${editingItem?.id}`,
            `${updatedMaterials.length} matérias-primas importadas do produto ${sourceProduct.code} - ${sourceProduct.commercialName} para o projeto ${editingItem?.code}`
        );

        addToast(t('pages.projectStatus.importModal.importSuccess', { count: updatedMaterials.length }), 'Success');
    };

    // --- Função para exportar INCI Names ---
    const handleExportRawMaterials = () => {
        if (rawMaterialsList.length === 0) return;

        const inciNames = rawMaterialsList
            .map((mp) => {
                let mpData;
                if (mp.isSemiAcabado) {
                    mpData = allProducts.find(p => p.id === mp.id);
                } else {
                    mpData = allRawMaterials.find(m => m.id === mp.id);
                }

                if (!mpData) return null;

                return mpData.inciName || mp.inciName || '';
            })
            .filter(name => name);

        const textContent = inciNames.join(';');

        const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const fileName = `inci-names-${editingItem?.code || 'projeto'}-${new Date().toISOString().split('T')[0]}.txt`;

        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // --- Handler para gerar Processo de Manipulação - abre o modal ---
    const handleGenerateProcessoManipulacao = (project) => {
        if (!can.view) {
            addToast(t('pages.helpdesk.noPermissionCreate'), 'Error');
            return;
        }

        if (!project.rawMaterials || project.rawMaterials.length === 0) {
            addToast(t('pages.projectStatus.manipulationStep.noMaterials'), 'Error');
            return;
        }

        if (!project.preparationSteps || project.preparationSteps.length === 0) {
            addToast(t('pages.projectStatus.manipulationStep.noPreparationSteps'), 'Error');
            return;
        }

        setSelectedProjectForManipulation(project);
        setManipulationData({
            calculadaKgl: '',
            perda: '',
            programadaKgl: ''
        });
        setShowManipulationModal(true);
    };

    // --- Função para confirmar a geração após preenchimento do modal ---
    const handleConfirmManipulationGeneration = () => {
        const project = selectedProjectForManipulation;
        if (!project) return;

        if (!manipulationData.calculadaKgl || !manipulationData.perda || !manipulationData.programadaKgl) {
            addToast(t('pages.projectStatus.manipulationStep.fillAllFields'), 'Error');
            return;
        }

        try {
            const safeAllRawMaterials = allRawMaterials || [];
            const safeAllCategories = allCategories || [];

            const productData = {
                code: project.code || '',
                commercialName: project.projectName || '',
                netWeight: project.netWeight || '',
                grossWeight: project.grossWeight || '',
                density: project.density || '',
                lossIndex: project.lossIndex || '',
                markup: project.markup || '1.00',
                rawMaterials: project.rawMaterials || [],
                qualityCriteria: project.qualityCriteria || [],
                preparationSteps: project.preparationSteps || [],
                steps: project.steps || {},
                gramatura: project.gramatura || '',
                gramaturaUnit: project.gramaturaUnit || 'g',
                totalFormulationCost: project.totalFormulationCost || 0,
                calculadaKgl: manipulationData.calculadaKgl,
                perda: manipulationData.perda,
                programadaKgl: manipulationData.programadaKgl,
            };

            const orderData = {
                id: project.id || '',
                opCode: project.code || 'N/A',
                items: [{
                    productId: project.id || 'project-' + Date.now(),
                    productCode: project.code || '',
                    productName: project.projectName || '',
                    quantity: 1,
                    mode: 'mass',
                    lote: project.code || 'N/A',
                    obs: project.steps?.manipulation?.data?.obs || '',
                }],
                productData: productData,
                status: project.situation || 'Active',
                clientName: project.clientName || '',
                projectName: project.projectName || '',
                code: project.code || '',
                manipulationData: manipulationData,
            };

            exportProcessoManipulacao({
                order: orderData,
                allRawMaterials: safeAllRawMaterials,
                allCategories: safeAllCategories,
                allProducts: allProducts,
                allProductTypes: allProductTypes,
                showNotification: addToast
            });

            logActivity(
                'manipulation_document_generated',
                `development-project/${project.id}`,
                `Processo de Manipulação gerado para o projeto ${project.code} - ${project.projectName}`
            );

            setShowManipulationModal(false);
            setSelectedProjectForManipulation(null);

        } catch (error) {
            console.error('Error generating Processo de Manipulação:', error);
            addToast(`${t('pages.projectStatus.manipulationStep.generateError')}: ${error.message}`, 'Error');
        }
    };

    // --- Handler para criar produto a partir do projeto aprovado ---
    const handleCreateProductFromProject = async (productDataToSave) => {
        if (!productDataToSave) return;

        setIsCreatingProduct(true);
        setFormError('');

        try {
            const existingProduct = allProducts.find(p => p.code === productDataToSave.code);
            if (existingProduct) {
                setFormError(t('pages.projectStatus.productRegistration.codeExists', { code: productDataToSave.code }));
                setIsCreatingProduct(false);
                return;
            }

            const productsCollection = collection(db, PRODUCTS_COLLECTION);
            const docData = {
                ...productDataToSave,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                createdFromProjectId: editingItem?.id || productDataToSave.fromProjectId,
                createdFromProjectCode: productDataToSave.fromProjectCode,
                isFromProject: true,
                rawMaterials: productDataToSave.rawMaterials || [],
                qualityCriteria: productDataToSave.qualityCriteria || [],
                preparationSteps: productDataToSave.preparationSteps || [],
                productTypeIds: productDataToSave.productTypeIds || [],
                categoryIds: productDataToSave.categoryIds || [],
            };

            const docRef = await addDoc(productsCollection, docData);
            const newProductId = docRef.id;

            await logActivity(
                'product_created_from_project',
                `products/${newProductId}`,
                `Produto ${productDataToSave.code} - ${productDataToSave.commercialName} criado a partir do projeto ${editingItem?.code || productDataToSave.fromProjectCode}`
            );

            addToast(t('pages.projectStatus.productRegistration.success'), 'Success');

            // ✅ NOTIFICAR SOBRE REGISTRO DE PRODUTO
            if (editingItem) {
                await notifyProjectStatusChange(editingItem, 'status', 'product_registered', 'product_registered');
            }

            setShowProductRegistrationModal(false);
            setProductData(null);

            const productsSnapshot = await getDocs(collection(db, PRODUCTS_COLLECTION));
            const updatedProducts = productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllProducts(updatedProducts);

            if (editingItem) {
                await updateDocument(editingItem.id, {
                    productCreated: true,
                    productCode: productDataToSave.code,
                    productId: newProductId,
                    productCreatedAt: new Date().toISOString(),
                    steps: {
                        ...(editingItem.steps || {}),
                        status: {
                            ...(editingItem.steps?.status || {}),
                            status: 'product_registered',
                            productRegisteredAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString()
                        }
                    }
                });

                setEditingItem(prev => prev ? {
                    ...prev,
                    productCreated: true,
                    productCode: productDataToSave.code,
                    productId: newProductId,
                    productCreatedAt: new Date().toISOString(),
                    steps: {
                        ...(prev.steps || {}),
                        status: {
                            ...(prev.steps?.status || {}),
                            status: 'product_registered',
                            productRegisteredAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString()
                        }
                    }
                } : prev);
            }

            if (refreshProjects) {
                await refreshProjects();
            }

            setIsCreatingProduct(false);
            setProductCreated(true);

        } catch (error) {
            console.error('Erro ao criar produto:', error);
            setFormError(`${t('pages.projectStatus.productRegistration.error')}: ${error.message}`);
            addToast(`${t('pages.projectStatus.productRegistration.error')}: ${error.message}`, 'Error');
            setIsCreatingProduct(false);
        }
    };

    // --- Handlers para Status da Amostra ---
    const handleApproveSample = async () => {
        if (!editingItem) return;

        if (editingItem.productCreated) {
            addToast(t('pages.projectStatus.statusStep.productAlreadyRegistered'), 'Info');
            return;
        }

        await updateStepStatus(editingItem.id, 'status', 'approved');

        await logActivity(
            'sample_approved',
            `development-project/${editingItem.id}`,
            `Amostra do projeto ${editingItem.code} - ${editingItem.projectName} aprovada pelo cliente`
        );

        addToast(t('pages.projectStatus.statusStep.approvedSuccess'), 'Success');

        const productDataPrepared = prepareProductDataFromProject(editingItem);
        if (productDataPrepared) {
            setProductData(productDataPrepared);
            setShowProductRegistrationModal(true);
        }
    };

    const handleRejectSample = async () => {
        if (!editingItem) return;

        if (editingItem.productCreated) {
            addToast(t('pages.projectStatus.statusStep.cannotReject'), 'Error');
            return;
        }

        const updatedSteps = {
            ...(editingItem.steps || {}),
            formula: {
                ...(editingItem.steps?.formula || {}),
                status: 'pending',
                updatedAt: new Date().toISOString()
            },
            manipulation: {
                ...(editingItem.steps?.manipulation || {}),
                status: 'pending',
                updatedAt: new Date().toISOString()
            },
            quality: {
                ...(editingItem.steps?.quality || {}),
                status: 'pending',
                updatedAt: new Date().toISOString()
            },
            client: {
                ...(editingItem.steps?.client || {}),
                status: 'pending',
                updatedAt: new Date().toISOString()
            },
            status: {
                status: 'rejected',
                updatedAt: new Date().toISOString(),
                rejectedAt: new Date().toISOString()
            }
        };

        try {
            await updateDocument(editingItem.id, { steps: updatedSteps });
            setEditingItem(prev => prev ? { ...prev, steps: updatedSteps } : prev);
            setCurrentStep('formula');

            // ✅ NOTIFICAR SOBRE REJEIÇÃO
            await notifyProjectStatusChange(editingItem, 'status', 'rejected', 'rejected');

            await logActivity(
                'sample_rejected',
                `development-project/${editingItem.id}`,
                `Amostra do projeto ${editingItem.code} - ${editingItem.projectName} rejeitada pelo cliente`
            );

            addToast(t('pages.projectStatus.statusStep.rejectedSuccess'), 'Info');
            setShowProductRegistrationModal(false);
            setProductData(null);
        } catch (error) {
            addToast(`${t('pages.projectStatus.statusStep.rejectError')}: ${error.message}`, 'Error');
        }
    };

    // --- Reset do Formulário ---
    const resetForm = () => {
        setEditingItem(null);
        setRawMaterialsList([]);
        setQualityCriteria([]);
        setPreparationSteps([]);
        setCurrentStepText('');
        setSelectedSetup('');
        setMarkup('1.00');
        setFormError('');
        setFormSuccess('');
        setFilterProductTypeId('');
        setFilterCategoryId('');
        setShowImportMPModal(false);
        setImportSearchTerm('');
        setCurrentStep('formula');
        setQualityAnalyses(DEFAULT_QUALITY_ANALYSES.map(analysis => ({
            ...analysis,
            value: '',
            observations: '',
        })));
        originalItemDataRef.current = null;
        setProductCreated(false);
    };

    // --- Verificar se o formulário foi alterado ---
    const isFormDirty = useCallback(() => {
        if (!editingItem) return true;
        if (!originalItemDataRef.current) return true;

        const original = originalItemDataRef.current;

        const rawMaterialsChanged = JSON.stringify(original.rawMaterials || []) !== JSON.stringify(rawMaterialsList);
        if (rawMaterialsChanged) return true;

        const criteriaChanged = JSON.stringify(original.qualityCriteria || []) !== JSON.stringify(qualityCriteria);
        if (criteriaChanged) return true;

        const stepsChanged = JSON.stringify(original.preparationSteps || []) !== JSON.stringify(preparationSteps);
        if (stepsChanged) return true;

        const markupChanged = parseFloat(original.markup || 1) !== parseFloat(markup || 1);
        if (markupChanged) return true;

        const originalAnalyses = original.qualityAnalyses || DEFAULT_QUALITY_ANALYSES.map(a => ({ ...a, value: '', observations: '' }));
        const analysesChanged = JSON.stringify(originalAnalyses) !== JSON.stringify(qualityAnalyses);
        if (analysesChanged) return true;

        const originalDate = original.analysisDate || '';
        const currentDate = editingItem?.steps?.quality?.data?.analysisDate || '';
        if (originalDate !== currentDate) return true;

        const originalAnalyst = original.analyst || '';
        const currentAnalyst = editingItem?.steps?.quality?.data?.analyst || '';
        if (originalAnalyst !== currentAnalyst) return true;

        return false;
    }, [editingItem, rawMaterialsList, qualityCriteria, preparationSteps, markup, qualityAnalyses]);

    // --- Função para atualizar o status do passo ---
    const updateStepStatus = useCallback(async (projectId, stepId, status) => {
        if (!can.edit) {
            addToast(t('pages.development.noPermissionEdit'), 'Error');
            return;
        }

        try {
            const project = projects?.find(p => p.id === projectId);
            if (!project) return;

            const updatedSteps = {
                ...(project.steps || {}),
                [stepId]: {
                    ...(project.steps?.[stepId] || {}),
                    status: status,
                    updatedAt: new Date().toISOString()
                }
            };

            await updateDocument(projectId, { steps: updatedSteps });

            // ✅ NOTIFICAR SOBRE MUDANÇA DE STATUS
            await notifyProjectStatusChange(project, stepId, status, 'step_completed');

            // Get translated step label
            const stepLabels = {
                'formula': t('pages.development.projectSteps.formula'),
                'manipulation': t('pages.development.projectSteps.manipulation'),
                'quality': t('pages.development.projectSteps.quality'),
                'client': t('pages.development.projectSteps.client'),
                'status': t('pages.development.projectSteps.status')
            };
            const stepLabel = stepLabels[stepId] || stepId;

            await logActivity(
                'project_step_completed',
                `development-project/${projectId}/step/${stepId}`,
                `Passo "${stepLabel}" do projeto ${project.code} concluído (Status: ${status})`
            );

            addToast(`${t('pages.projectStatus.stepUpdated')} "${stepLabel}" ${t('pages.projectStatus.toStatus')} ${status}`, 'Success');

            setEditingItem(prev => prev ? { ...prev, steps: updatedSteps } : prev);
        } catch (error) {
            addToast(`${t('pages.projectStatus.stepUpdateError')}: ${error.message}`, 'Error');
        }
    }, [projects, updateDocument, addToast, can.edit, t, notifyProjectStatusChange]);

    // --- Salvar Projeto ---
    const handleSave = async () => {
        if (!can.edit) {
            setFormError(t('pages.development.noPermissionEdit'));
            addToast(t('pages.development.noPermissionEdit'), 'Error');
            await logActivity(
                'project_status_updated',
                `development-project/${editingItem.id}`,
                `Status do projeto ${editingItem.code} - ${editingItem.projectName} atualizado (Passo: ${currentStep})`
            );
            return;
        }

        setFormError('');
        setFormSuccess('');

        if (!editingItem) {
            setFormError(t('pages.projectStatus.noProjectSelected'));
            return;
        }

        if (!isFormDirty()) {
            setFormSuccess(t('pages.projectStatus.noChanges'));
            setTimeout(() => {
                resetForm();
                setIsModalOpen(false);
            }, 1000);
            return;
        }

        const unitCategoryIds = allCategories
            .filter(c => ['SERIGRAFIA', 'EMBALAGEM'].includes(c.name?.toUpperCase()))
            .map(c => c.id);

        const isUnitCalculation = (mpId) => {
            const mpData = allRawMaterials.find(item => item.id === mpId);
            return mpData?.categoryIds?.some(id => unitCategoryIds.includes(id));
        };

        const totalQuantity = rawMaterialsList.reduce((acc, mp) => {
            if (!isUnitCalculation(mp.id)) {
                return acc + (parseFloat(mp.quantity) || 0);
            }
            return acc;
        }, 0);

        const hasPercentageMaterials = rawMaterialsList.some(mp => {
            return !isUnitCalculation(mp.id);
        });

        if (hasPercentageMaterials && Math.abs(totalQuantity - 100) > 0.01) {
            setFormError(t('pages.projectStatus.percentageSumError', { total: totalQuantity.toFixed(2) }));
            return;
        }

        setIsSubmitting(true);

        try {
            const rawMaterialsCost = rawMaterialsList.reduce((acc, mp) => {
                if (!isUnitCalculation(mp.id)) {
                    const price = getLatestPrice(mp.id, mp.valor);
                    const quantity = parseFloat(mp.quantity) || 0;
                    return acc + (price * quantity) / 100;
                }
                return acc;
            }, 0);

            const netWeightVal = parseFloat(editingItem.netWeight) || 0;
            const rawMaterialsTotalCost = rawMaterialsCost * netWeightVal;

            const unitMaterialsCost = rawMaterialsList.reduce((acc, mp) => {
                if (isUnitCalculation(mp.id)) {
                    const price = getLatestPrice(mp.id, mp.valor);
                    const quantity = parseFloat(mp.quantity) || 0;
                    return acc + (price * quantity);
                }
                return acc;
            }, 0);

            const currentTotalCost = rawMaterialsTotalCost + unitMaterialsCost;

            const currentStepStatus = 'completed';

            const stepData = {
                rawMaterials: rawMaterialsList,
                qualityCriteria: qualityCriteria,
                preparationSteps: preparationSteps,
                markup: parseFloat(markup) || 1,
                totalFormulationCost: currentTotalCost,
            };

            if (currentStep === 'quality') {
                stepData.qualityAnalyses = qualityAnalyses;
                stepData.analysisDate = editingItem?.steps?.quality?.data?.analysisDate || '';
                stepData.analyst = editingItem?.steps?.quality?.data?.analyst || '';
            }

            const updatedSteps = {
                ...(editingItem.steps || {}),
                [currentStep]: {
                    ...(editingItem.steps?.[currentStep] || {}),
                    status: currentStepStatus,
                    updatedAt: new Date().toISOString(),
                    data: stepData
                }
            };

            const qualityData = currentStep === 'quality' ? {
                qualityAnalyses: qualityAnalyses,
                analysisDate: editingItem?.steps?.quality?.data?.analysisDate || '',
                analyst: editingItem?.steps?.quality?.data?.analyst || '',
            } : {};

            const updatedData = {
                ...editingItem,
                rawMaterials: rawMaterialsList,
                qualityCriteria: qualityCriteria,
                preparationSteps: preparationSteps,
                markup: parseFloat(markup) || 1,
                totalFormulationCost: currentTotalCost,
                steps: updatedSteps,
                updatedAt: new Date().toISOString(),
                ...qualityData,
            };

            const result = await updateDocument(editingItem.id, updatedData);

            if (result.success) {
                setFormSuccess(t('pages.development.updateSuccess'));
                addToast(t('pages.development.updateSuccess'), 'Success');
                setTimeout(() => {
                    resetForm();
                    setIsModalOpen(false);
                }, 1500);
            } else {
                setFormError(`${t('pages.development.updateError')}: ${result.error}`);
                addToast(`${t('pages.development.updateError')}: ${result.error}`, 'Error');
            }
        } catch (error) {
            setFormError(`${t('pages.projectStatus.error')}: ${error.message}`);
            addToast(`${t('pages.projectStatus.error')}: ${error.message}`, 'Error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Abrir Modal de Edição ---
    const handleEditClick = (item) => {
        if (!can.edit) {
            addToast(t('pages.development.noPermissionEdit'), 'Error');
            return;
        }

        const savedAnalyses = item.qualityAnalyses || item.steps?.quality?.data?.qualityAnalyses || null;

        let analysesToLoad;
        if (savedAnalyses && savedAnalyses.length > 0) {
            analysesToLoad = DEFAULT_QUALITY_ANALYSES.map(defaultAnalysis => {
                const saved = savedAnalyses.find(a => a.id === defaultAnalysis.id);
                return saved ? { ...saved } : { ...defaultAnalysis, value: '', observations: '' };
            });
        } else {
            analysesToLoad = DEFAULT_QUALITY_ANALYSES.map(analysis => ({
                ...analysis,
                value: '',
                observations: '',
            }));
        }

        const rawMaterialsWithCommercialName = (item.rawMaterials || []).map(mp => ({
            ...mp,
            commercialName: mp.commercialName || mp.inciName || 'N/A'
        }));

        originalItemDataRef.current = {
            rawMaterials: rawMaterialsWithCommercialName,
            qualityCriteria: item.qualityCriteria || [],
            preparationSteps: item.preparationSteps || [],
            markup: item.markup?.toString() || '1.00',
            qualityAnalyses: analysesToLoad,
            analysisDate: item.steps?.quality?.data?.analysisDate || '',
            analyst: item.steps?.quality?.data?.analyst || '',
        };

        setEditingItem(item);
        setRawMaterialsList(rawMaterialsWithCommercialName);
        setQualityCriteria(item.qualityCriteria || []);
        setPreparationSteps(item.preparationSteps || []);
        setMarkup(item.markup?.toString() || '1.00');
        setQualityAnalyses(analysesToLoad);
        setFormError('');
        setFormSuccess('');
        setProductCreated(item.productCreated || false);

        const steps = item.steps || {};
        const firstIncomplete = PROJECT_STEPS.find(s => steps[s.id]?.status !== 'completed' && steps[s.id]?.status !== 'approved' && steps[s.id]?.status !== 'product_registered');
        setCurrentStep(firstIncomplete?.id || 'formula');
        setIsModalOpen(true);
    };

    // --- Navegação entre passos ---
    const handleStepChange = (stepId) => {
        setCurrentStep(stepId);
    };

    // --- Renderizar conteúdo do passo atual ---
    const renderStepContent = () => {
        switch (currentStep) {
            case 'formula':
                return renderFormulaStep();
            case 'manipulation':
                return renderManipulationStep();
            case 'quality':
                return renderQualityStep();
            case 'client':
                return renderClientStep();
            case 'status':
                return renderStatusStep();
            default:
                return renderFormulaStep();
        }
    };

    // --- Componente de Modo de Preparo (extraído para reuso) ---
    const renderPreparationStepsComponent = () => {
        const activeSetups = preparationSetups.filter(setup =>
            setup.situation === 'Ativo' && setup.preparationSteps?.length > 0
        );

        return (
            <>
                {activeSetups.length > 0 && (
                    <div className={styles.inputGroup} style={{ marginBottom: '15px' }}>
                        <label htmlFor="setupSelector">{t('pages.projectStatus.manipulationStep.loadSetup')}</label>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <select
                                id="setupSelector"
                                value={selectedSetup}
                                onChange={(e) => handleLoadSetup(e.target.value)}
                                className={styles.input}
                                style={{ flex: 1 }}
                                disabled={isSubmitting || !can.edit}
                            >
                                <option value="">{t('pages.projectStatus.manipulationStep.selectSetup')}</option>
                                {activeSetups.map(setup => (
                                    <option key={setup.id} value={setup.id}>
                                        {setup.name} ({setup.preparationSteps.length} {t('pages.projectStatus.manipulationStep.steps')})
                                    </option>
                                ))}
                            </select>
                            {selectedSetup && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedSetup('');
                                        setPreparationSteps([]);
                                    }}
                                    className={styles.cancelButton}
                                    style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}
                                >
                                    <FiX size={16} /> {t('pages.projectStatus.manipulationStep.clearSetup')}
                                </button>
                            )}
                        </div>
                    </div>
                )}

                <div className={styles.inputGroup} style={{ marginBottom: '15px' }}>
                    <label htmlFor="preparationStep">{t('pages.projectStatus.manipulationStep.addStepManually')}</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <input
                            id="preparationStep"
                            type="text"
                            value={currentStepText}
                            onChange={(e) => setCurrentStepText(e.target.value)}
                            onKeyPress={handleStepKeyPress}
                            className={styles.input}
                            placeholder={t('pages.projectStatus.manipulationStep.stepPlaceholder')}
                            disabled={isSubmitting || !can.edit}
                            style={{ flex: 1 }}
                        />
                        <button
                            type="button"
                            onClick={handleAddPreparationStep}
                            className={styles.addButton}
                            disabled={isSubmitting || !currentStepText.trim() || !can.edit}
                            style={{
                                padding: '8px 16px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <FiPlus size={16} /> {t('pages.projectStatus.manipulationStep.addStep')}
                        </button>
                    </div>
                </div>

                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '80px', textAlign: 'center' }}>{t('pages.projectStatus.manipulationStep.order')}</th>
                                <th>{t('pages.projectStatus.manipulationStep.stepDescription')}</th>
                                <th style={{ width: '100px' }}>{t('pages.projectStatus.manipulationStep.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {preparationSteps.length === 0 ? (
                                <tr>
                                    <td colSpan="3" className={styles.emptyTable}>
                                        {activeSetups.length > 0 ? t('pages.projectStatus.manipulationStep.noStepsWithSetup') : t('pages.projectStatus.manipulationStep.noSteps')}
                                    </td>
                                </tr>
                            ) : (
                                preparationSteps.map((step, index) => (
                                    <tr key={step.id}>
                                        <td>
                                            <div className={styles.orderControls}>
                                                <button
                                                    type="button"
                                                    className={styles.orderButton}
                                                    onClick={() => handleMovePreparationStep(index, 'up')}
                                                    disabled={index === 0 || isSubmitting || !can.edit}
                                                >
                                                    <FiChevronUp size={12} />
                                                </button>
                                                <span style={{ fontWeight: 'bold', margin: '0 4px' }}>
                                                    {index + 1}
                                                </span>
                                                <button
                                                    type="button"
                                                    className={styles.orderButton}
                                                    onClick={() => handleMovePreparationStep(index, 'down')}
                                                    disabled={index === preparationSteps.length - 1 || isSubmitting || !can.edit}
                                                >
                                                    <FiChevronDown size={12} />
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                value={step.description}
                                                onChange={(e) => handleUpdatePreparationStep(step.id, e.target.value)}
                                                className={styles.input}
                                                disabled={isSubmitting || !can.edit}
                                                style={{ width: '100%' }}
                                            />
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={() => handleRemovePreparationStep(step.id)}
                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                disabled={isSubmitting || !can.edit}
                                                title={t('pages.projectStatus.manipulationStep.removeStep')}
                                            >
                                                <FiTrash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {preparationSteps.length > 0 && (
                    <div style={{
                        marginTop: '10px',
                        padding: '8px 12px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        color: '#6c757d',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span>{t('pages.projectStatus.manipulationStep.totalSteps')}: {preparationSteps.length}</span>
                        {selectedSetup && (
                            <span style={{ color: 'var(--primary-color)', fontWeight: '500' }}>
                                {t('pages.projectStatus.manipulationStep.setup')}: {preparationSetups.find(s => s.id === selectedSetup)?.name}
                            </span>
                        )}
                    </div>
                )}
            </>
        );
    };

    // --- Componente de Critérios de Qualidade (extraído para reuso) ---
    const renderQualityCriteriaComponent = () => {
        const qualityCriteriaOptions = allQualityCriteria.map(c => ({
            value: c.id,
            label: `${c.analysis} - ${c.method}`,
        }));

        const availableCriteria = qualityCriteriaOptions.filter(opt => !qualityCriteria.find(qc => qc.id === opt.value));
        return (
            <>
                <div className={styles.inputGroup} style={{ marginBottom: '15px' }}>
                    <label htmlFor="addCriteria">{t('pages.projectStatus.qualityStep.addCriteria')}</label>
                    <select id="addCriteria" onChange={handleAddCriteria} className={`${styles.input} ${styles.select}`} disabled={isSubmitting || auxiliaryDataLoading || availableCriteria.length === 0 || !can.edit} value={''}>
                        <option value="" disabled>{auxiliaryDataLoading ? t('common.loading') : (availableCriteria.length === 0) ? t('pages.projectStatus.qualityStep.noCriteria') : t('pages.projectStatus.qualityStep.selectCriteria')}</option>
                        {availableCriteria.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                </div>

                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '80px', textAlign: 'center' }}>{t('pages.projectStatus.qualityStep.order')}</th>
                                <th>{t('pages.projectStatus.qualityStep.analysis')}</th>
                                <th style={{ width: '250px' }}>{t('pages.projectStatus.qualityStep.specification')}</th>
                                <th>{t('pages.projectStatus.qualityStep.method')}</th>
                                <th style={{ width: '120px' }}>{t('pages.projectStatus.qualityStep.control')}</th>
                                <th style={{ width: '100px' }}>{t('pages.projectStatus.qualityStep.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {qualityCriteria.length === 0 ? (
                                <tr><td colSpan="6" className={styles.emptyTable}>{t('pages.projectStatus.qualityStep.noCriteriaAdded')}</td></tr>
                            ) : (
                                qualityCriteria.map((c, index) => (
                                    <tr key={c.id}>
                                        <td>
                                            <div className={styles.orderControls}>
                                                <button type="button" className={styles.orderButton} onClick={() => handleMoveCriteria(index, 'up')} disabled={index === 0 || isSubmitting || !can.edit}>
                                                    <FiChevronUp size={12} />
                                                </button>
                                                <span style={{ margin: '0 4px' }}>{index + 1}</span>
                                                <button type="button" className={styles.orderButton} onClick={() => handleMoveCriteria(index, 'down')} disabled={index === qualityCriteria.length - 1 || isSubmitting || !can.edit}>
                                                    <FiChevronDown size={12} />
                                                </button>
                                            </div>
                                        </td>
                                        <td>{c.analysis}</td>
                                        <td><input type="text" value={c.specification} onChange={(e) => handleUpdateCriteriaField(c.id, 'specification', e.target.value)} className={styles.input} disabled={isSubmitting || !can.edit} /></td>
                                        <td>{c.method}</td>
                                        <td>
                                            <select value={c.control} onChange={(e) => handleUpdateCriteriaField(c.id, 'control', e.target.value)} className={`${styles.input} ${styles.select}`} disabled={isSubmitting || !can.edit}>
                                                <option value="Processo">{t('pages.projectStatus.qualityStep.process')}</option>
                                                <option value="Produto acabado">{t('pages.projectStatus.qualityStep.finishedProduct')}</option>
                                                <option value="Ambos">{t('pages.projectStatus.qualityStep.both')}</option>
                                            </select>
                                        </td>
                                        <td>
                                            <button type="button" onClick={() => handleRemoveCriteria(c.id)} className={`${styles.actionButton} ${styles.deleteButton}`} disabled={isSubmitting || !can.edit} title={t('pages.projectStatus.qualityStep.removeCriteria')}>
                                                <FiTrash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    // --- Renderizar Análises Organolépticas e Físico-Químicas ---
    const renderQualityAnalyses = () => {
        return (
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <span className={styles.cardIcon}><FiBox size={18} /></span>
                    <h4 className={styles.cardTitle}>{t('pages.projectStatus.qualityStep.analyses')}</h4>
                </div>
                <div className={styles.cardBody}>
                    <div className={styles.analysesGrid}>
                        {qualityAnalyses.map(analysis => (
                            <div key={analysis.id} className={styles.analysisRow}>
                                <div className={styles.analysisLabel}>
                                    <span className={styles.analysisName}>{analysis.label}</span>
                                    {analysis.unit && (
                                        <span className={styles.analysisUnit}>({analysis.unit})</span>
                                    )}
                                </div>
                                <div className={styles.analysisFields}>
                                    <input
                                        type="text"
                                        value={analysis.value}
                                        onChange={(e) => handleUpdateQualityAnalysis(analysis.id, 'value', e.target.value)}
                                        className={`${styles.input} ${styles.analysisInput}`}
                                        placeholder={t('pages.projectStatus.qualityStep.valuePlaceholder')}
                                        disabled={isSubmitting || !can.edit}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className={styles.inputGroup} style={{ marginTop: '15px' }}>
                        <label htmlFor="qualityObservations">{t('pages.projectStatus.qualityStep.observations')}</label>
                        <textarea
                            id="qualityObservations"
                            value={qualityAnalyses[0]?.observations || ''}
                            onChange={(e) => {
                                setQualityAnalyses(prev => prev.map(a => ({
                                    ...a,
                                    observations: e.target.value
                                })));
                            }}
                            className={styles.textarea}
                            rows={3}
                            placeholder={t('pages.projectStatus.qualityStep.observationsPlaceholder')}
                            disabled={isSubmitting || !can.edit}
                        />
                    </div>
                </div>
            </div>
        );
    };

    // --- Renderizar Modal de Dados de Manipulação ---
    const renderManipulationDataModal = () => {
        if (!showManipulationModal) return null;

        const updateManipulationData = (field, value) => {
            let newData = { ...manipulationData, [field]: value };

            if (field === 'calculadaKgl' || field === 'perda') {
                const calculada = parseFloat(newData.calculadaKgl) || 0;
                const perda = parseFloat(newData.perda) || 0;
                const programada = calculada * (1 + perda / 100);
                newData.programadaKgl = programada;
            }

            setManipulationData(newData);
        };

        return (
            <div className={styles.modalBackdrop}>
                <div className={styles.modalContainer} style={{ maxWidth: '500px' }}>
                    <div className={styles.modalHeader}>
                        <div>
                            <h3 className={styles.modalTitle}>{t('pages.projectStatus.manipulationStep.modalTitle')}</h3>
                            <p className={styles.modalSubtitle}>
                                {t('pages.projectStatus.manipulationStep.modalSubtitle')}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setShowManipulationModal(false);
                                setSelectedProjectForManipulation(null);
                            }}
                            className={styles.modalCloseButton}
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    <div className={styles.modalBody}>
                        <div className={styles.formGrid}>
                            <div className={styles.inputGroup}>
                                <label htmlFor="calculadaKgl">{t('pages.projectStatus.manipulationStep.calculadaKgl')}</label>
                                <input
                                    id="calculadaKgl"
                                    type="number"
                                    step="0.001"
                                    className={styles.input}
                                    value={manipulationData.calculadaKgl}
                                    onChange={(e) => updateManipulationData('calculadaKgl', e.target.value)}
                                    placeholder={t('pages.projectStatus.manipulationStep.calculadaKglPlaceholder')}
                                    required
                                />
                                <small className={styles.fieldHelp}>{t('pages.projectStatus.manipulationStep.calculadaKglHelp')}</small>
                            </div>

                            <div className={styles.inputGroup}>
                                <label htmlFor="perda">{t('pages.projectStatus.manipulationStep.perda')}</label>
                                <input
                                    id="perda"
                                    type="number"
                                    step="0.01"
                                    className={styles.input}
                                    value={manipulationData.perda}
                                    onChange={(e) => updateManipulationData('perda', e.target.value)}
                                    placeholder={t('pages.projectStatus.manipulationStep.perdaPlaceholder')}
                                    required
                                />
                                <small className={styles.fieldHelp}>{t('pages.projectStatus.manipulationStep.perdaHelp')}</small>
                            </div>

                            <div className={styles.inputGroup}>
                                <label htmlFor="programadaKgl">{t('pages.projectStatus.manipulationStep.programadaKgl')}</label>
                                <input
                                    id="programadaKgl"
                                    type="number"
                                    step="0.001"
                                    className={styles.input}
                                    value={manipulationData.programadaKgl}
                                    readOnly
                                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                                    placeholder={t('pages.projectStatus.manipulationStep.programadaKglHelp')}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <button
                            type="button"
                            onClick={() => {
                                setShowManipulationModal(false);
                                setSelectedProjectForManipulation(null);
                            }}
                            className={styles.secondaryButton}
                        >
                            <FiX size={18} /> {t('pages.projectStatus.manipulationStep.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirmManipulationGeneration}
                            className={styles.primaryButton}
                            disabled={
                                !manipulationData.calculadaKgl ||
                                !manipulationData.perda ||
                                !manipulationData.programadaKgl
                            }
                        >
                            <FiFileText size={18} /> {t('pages.projectStatus.manipulationStep.generate')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // --- Renderizar Passo de Fórmula ---
    const renderFormulaStep = () => {
        const unitCategoryIds = allCategories
            .filter(c => ['SERIGRAFIA', 'EMBALAGEM'].includes(c.name?.toUpperCase()))
            .map(c => c.id);

        const isUnitCalculation = (mpId) => {
            const mpData = allRawMaterials.find(item => item.id === mpId);
            return mpData?.categoryIds?.some(id => unitCategoryIds.includes(id));
        };

        const calculateLineTotal = (mpId, valorAtual, quantity) => {
            const v = parseFloat(valorAtual) || 0;
            const q = parseFloat(quantity) || 0;

            if (isUnitCalculation(mpId)) {
                return v * q;
            } else {
                return (v * q) / 100;
            }
        };

        const rawMaterialsCost = rawMaterialsList.reduce((acc, mp) => {
            if (!isUnitCalculation(mp.id)) {
                const currentPrice = getLatestPrice(mp.id, mp.valor);
                const quantity = parseFloat(mp.quantity) || 0;
                return acc + (currentPrice * quantity) / 100;
            }
            return acc;
        }, 0);

        const netWeightVal = parseFloat(editingItem?.netWeight) || 0;
        const rawMaterialsTotalCost = rawMaterialsCost * netWeightVal;

        const unitMaterialsCost = rawMaterialsList.reduce((acc, mp) => {
            if (isUnitCalculation(mp.id)) {
                const currentPrice = getLatestPrice(mp.id, mp.valor);
                const quantity = parseFloat(mp.quantity) || 0;
                return acc + (currentPrice * quantity);
            }
            return acc;
        }, 0);

        const totalCost = rawMaterialsTotalCost + unitMaterialsCost;

        const totalPercentageQuantity = rawMaterialsList.reduce((acc, mp) => {
            if (!isUnitCalculation(mp.id)) {
                return acc + (parseFloat(mp.quantity) || 0);
            }
            return acc;
        }, 0);

        const hasPercentageMaterials = rawMaterialsList.some(mp => !isUnitCalculation(mp.id));
        const isQuantityValid = !hasPercentageMaterials || Math.abs(totalPercentageQuantity - 100) <= 0.01;
        const finalClientValue = totalCost * (parseFloat(markup) || 1);

        return (
            <div className={styles.stepContent}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <span className={styles.cardIcon}><FiPackage size={18} /></span>
                        <h4 className={styles.cardTitle}>{t('pages.projectStatus.formulaStep.title')}</h4>
                        <div className={styles.cardActions}>
                            {rawMaterialsList.length > 0 && can.view && (
                                <button
                                    type="button"
                                    onClick={handleExportRawMaterials}
                                    className={`${styles.actionButton} ${styles.exportButton}`}
                                    title={t('pages.projectStatus.formulaStep.exportList')}
                                >
                                    <FiDownload size={14} />
                                </button>
                            )}
                            {can.edit && (
                                <button
                                    type="button"
                                    onClick={() => setShowImportMPModal(true)}
                                    className={`${styles.actionButton} ${styles.importButton}`}
                                    title={t('pages.projectStatus.formulaStep.importFromProduct')}
                                >
                                    <FiUpload size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className={styles.cardBody}>
                        <div className={styles.filterRow}>
                            <div className={styles.inputGroup} style={{ flex: 1 }}>
                                <label htmlFor="filterType">{t('pages.projectStatus.formulaStep.filterByType')}</label>
                                <select
                                    id="filterType"
                                    value={filterProductTypeId}
                                    onChange={(e) => setFilterProductTypeId(e.target.value)}
                                    className={`${styles.input} ${styles.select}`}
                                    disabled={isSubmitting || auxiliaryDataLoading || !can.edit}
                                >
                                    <option value="">{t('pages.projectStatus.formulaStep.allTypes')}</option>
                                    {allProductTypes && allProductTypes.map(type => (
                                        <option key={type.id} value={type.id}>{type.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className={styles.inputGroup} style={{ flex: 1 }}>
                                <label htmlFor="filterCategory">{t('pages.projectStatus.formulaStep.filterByCategory')}</label>
                                <select
                                    id="filterCategory"
                                    value={filterCategoryId}
                                    onChange={(e) => setFilterCategoryId(e.target.value)}
                                    className={`${styles.input} ${styles.select}`}
                                    disabled={isSubmitting || auxiliaryDataLoading || !can.edit}
                                >
                                    <option value="">{t('pages.projectStatus.formulaStep.allCategories')}</option>
                                    {allCategories && allCategories.map(category => (
                                        <option key={category.id} value={category.id}>{category.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className={styles.inputGroup} style={{ marginBottom: '15px' }}>
                            <label htmlFor="addRawMaterial">{t('pages.projectStatus.formulaStep.addRawMaterial')}</label>
                            <Select
                                id="addRawMaterial"
                                onChange={handleAddRawMaterial}
                                options={rawMaterialOptions}
                                classNamePrefix="react-select"
                                placeholder={
                                    auxiliaryDataLoading ? t('common.loading') :
                                        (rawMaterialOptions.length === 0) ? t('pages.projectStatus.formulaStep.noOptions') : t('pages.projectStatus.formulaStep.searchPlaceholder')
                                }
                                isDisabled={isSubmitting || auxiliaryDataLoading || rawMaterialOptions.length === 0 || !can.edit}
                                value={null}
                                isSearchable={true}
                            />
                        </div>

                        {rawMaterialsList.length > 0 && (
                            <div className={styles.percentageSummary}>
                                <div className={styles.summaryItem}>
                                    <span className={styles.summaryLabel}>{t('pages.projectStatus.formulaStep.totalMaterials')}</span>
                                    <span className={styles.summaryValue}>{rawMaterialsList.length}</span>
                                </div>
                                <div className={styles.summaryItem}>
                                    <span className={styles.summaryLabel}>{t('pages.projectStatus.formulaStep.percentageSum')}</span>
                                    <span className={styles.summaryValue} style={{
                                        color: isQuantityValid ? '#2e7d32' : '#f44336',
                                        fontWeight: 'bold'
                                    }}>
                                        {totalPercentageQuantity.toFixed(2)}%
                                    </span>
                                </div>
                                {hasPercentageMaterials && !isQuantityValid && (
                                    <div className={styles.warningMessage}>
                                        <FiAlertCircle size={14} /> {t('pages.projectStatus.formulaStep.warningPercentage', { total: totalPercentageQuantity.toFixed(2) })}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '80px', textAlign: 'center' }}>{t('pages.projectStatus.formulaStep.order')}</th>
                                        <th style={{ width: '80px' }}>{t('pages.projectStatus.formulaStep.phase')}</th>
                                        <th>{t('pages.projectStatus.formulaStep.commercialName')}</th>
                                        <th style={{ width: '120px' }}>{t('pages.projectStatus.formulaStep.cost')}</th>
                                        <th style={{ width: '100px' }}>{t('pages.projectStatus.formulaStep.quantity')}</th>
                                        <th style={{ width: '120px' }}>{t('pages.projectStatus.formulaStep.total')}</th>
                                        <th style={{ width: '100px' }}>{t('pages.projectStatus.formulaStep.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rawMaterialsList.length === 0 ? (
                                        <tr><td colSpan="7" className={styles.emptyTable}>{t('pages.projectStatus.formulaStep.noMaterials')}</td></tr>
                                    ) : (
                                        <>
                                            {rawMaterialsList.map((mp, index) => {
                                                const currentPrice = getLatestPrice(mp.id, mp.valor);
                                                const lineTotal = calculateLineTotal(mp.id, currentPrice, mp.quantity);
                                                const isUnit = isUnitCalculation(mp.id);

                                                return (
                                                    <tr key={mp.rowId}>
                                                        <td>
                                                            <div className={styles.orderControls}>
                                                                <button type="button" className={styles.orderButton} onClick={() => handleMoveRawMaterial(index, 'up')} disabled={index === 0 || isSubmitting || !can.edit}>
                                                                    <FiChevronUp size={12} />
                                                                </button>
                                                                <span style={{ fontWeight: 'bold', margin: '0 4px' }}>{index + 1}</span>
                                                                <button type="button" className={styles.orderButton} onClick={() => handleMoveRawMaterial(index, 'down')} disabled={index === rawMaterialsList.length - 1 || isSubmitting || !can.edit}>
                                                                    <FiChevronDown size={12} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                value={mp.fase}
                                                                onChange={(e) => handleUpdateRawMaterialField(mp.rowId, 'fase', e.target.value)}
                                                                className={styles.input}
                                                                disabled={isSubmitting || !can.edit}
                                                                style={{ width: '50px' }}
                                                            />
                                                        </td>
                                                        <td>{mp.commercialName || mp.inciName || 'N/A'}</td>
                                                        <td style={{ color: 'var(--primary-color)', fontWeight: '500' }}>
                                                            {currentPrice.toFixed(5)}
                                                        </td>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                <input
                                                                    type="number"
                                                                    step="0.0001"
                                                                    value={mp.quantity}
                                                                    onChange={(e) => handleUpdateRawMaterialField(mp.rowId, 'quantity', e.target.value)}
                                                                    className={styles.input}
                                                                    disabled={isSubmitting || !can.edit}
                                                                    style={{ width: '80px' }}
                                                                />
                                                                <span style={{ fontSize: '0.8rem', color: '#666' }}>
                                                                    {isUnit ? 'un' : '%'}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                                                            {lineTotal.toFixed(8)}
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveRawMaterial(mp.rowId)}
                                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                                disabled={isSubmitting || !can.edit}
                                                                title={t('pages.projectStatus.formulaStep.removeMaterial')}
                                                            >
                                                                <FiTrash2 size={14} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}

                                            <tr className={styles.tableFooterRow} style={{ backgroundColor: isQuantityValid ? '#f1f8e9' : '#fff8e1' }}>
                                                <td colSpan="4" className={styles.labelRight}>
                                                    <strong>{t('pages.projectStatus.formulaStep.quantity')}:</strong>
                                                </td>
                                                <td className={styles.valueCell} style={{
                                                    color: isQuantityValid ? '#2e7d32' : '#f44336',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {totalPercentageQuantity.toFixed(2)}%
                                                </td>
                                                <td colSpan="2"></td>
                                            </tr>

                                            {!isQuantityValid && (
                                                <tr className={styles.tableFooterRow} style={{ backgroundColor: '#fff8e1' }}>
                                                    <td colSpan="7" style={{ color: '#f44336', textAlign: 'center', padding: '8px', fontSize: '0.85rem' }}>
                                                        <FiAlertCircle size={14} style={{ marginRight: '6px' }} />
                                                        {t('pages.projectStatus.formulaStep.warningPercentage', { total: totalPercentageQuantity.toFixed(2) })}
                                                    </td>
                                                </tr>
                                            )}

                                            <tr className={styles.tableFooterRow}>
                                                <td colSpan="5" className={styles.labelRight}>
                                                    <strong>{t('pages.projectStatus.formulaStep.totalFormulationCost')}</strong>
                                                </td>
                                                <td className={styles.valueCell}>
                                                    {totalCost.toLocaleString('pt-BR', { minimumFractionDigits: 5 })}
                                                </td>
                                                <td></td>
                                            </tr>

                                            <tr className={styles.tableFooterRow}>
                                                <td colSpan="5" className={styles.labelRight}>{t('pages.projectStatus.formulaStep.markup')}</td>
                                                <td className={styles.valueCell}>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={markup}
                                                        onChange={(e) => setMarkup(e.target.value)}
                                                        className={styles.markupInputClean}
                                                        disabled={isSubmitting || !can.edit}
                                                    />
                                                </td>
                                                <td></td>
                                            </tr>

                                            <tr className={`${styles.tableFooterRow} ${styles.totalHighlight}`}>
                                                <td colSpan="5" className={styles.labelRight} style={{ color: 'inherit' }}>
                                                    {t('pages.projectStatus.formulaStep.suggestedPrice')}
                                                </td>
                                                <td className={styles.valueCell} style={{ color: 'inherit', fontSize: '1.1rem' }}>
                                                    {finalClientValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}
                                                </td>
                                                <td></td>
                                            </tr>
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <span className={styles.cardIcon}><FiList size={18} /></span>
                        <h4 className={styles.cardTitle}>{t('pages.projectStatus.manipulationStep.title')}</h4>
                    </div>
                    <div className={styles.cardBody}>
                        {renderPreparationStepsComponent()}
                    </div>
                </div>

                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <span className={styles.cardIcon}><FiCheck size={18} /></span>
                        <h4 className={styles.cardTitle}>{t('pages.projectStatus.qualityStep.title')}</h4>
                    </div>
                    <div className={styles.cardBody}>
                        {renderQualityCriteriaComponent()}
                    </div>
                </div>

                {can.edit && (
                    <div className={styles.stepActions}>
                        <button
                            type="button"
                            onClick={() => updateStepStatus(editingItem.id, 'formula', 'completed')}
                            className={styles.primaryButton}
                            disabled={isSubmitting}
                        >
                            <FiCheck size={18} /> {t('pages.projectStatus.formulaStep.markAsCompleted')}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // --- Renderizar Passo de Manipulação - SIMPLIFICADO ---
    const renderManipulationStep = () => {
        const isCompleted = editingItem?.steps?.manipulation?.status === 'completed';

        const handleGenerateFromModal = () => {
            if (editingItem) {
                handleGenerateProcessoManipulacao(editingItem);
            }
        };

        return (
            <div className={styles.stepContent}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <span className={styles.cardIcon}><FiClipboard size={18} /></span>
                        <h4 className={styles.cardTitle}>{t('pages.projectStatus.manipulationStep.title')}</h4>
                        {can.generate && editingItem?.preparationSteps?.length > 0 && (
                            <button
                                type="button"
                                onClick={handleGenerateFromModal}
                                className={`${styles.actionButton} ${styles.manipulationButton}`}
                                title={t('pages.projectStatus.manipulationStep.generateDocument')}
                            >
                                <FiFileText size={14} />
                            </button>
                        )}
                    </div>
                    <div className={styles.cardBody}>
                        <div className={styles.statusUpdateCard}>
                            <div className={styles.statusDisplay}>
                                <div className={styles.statusIndicator}>
                                    <span className={`${styles.statusDot} ${isCompleted ? styles.statusDotSuccess : styles.statusDotWarning}`}></span>
                                    <span className={styles.statusText}>
                                        {isCompleted ? t('pages.projectStatus.manipulationStep.completed') : t('pages.projectStatus.manipulationStep.inProgress')}
                                    </span>
                                </div>
                                {editingItem?.steps?.manipulation?.updatedAt && (
                                    <span className={styles.statusDate}>
                                        {t('pages.projectStatus.manipulationStep.updatedAt', { date: new Date(editingItem.steps.manipulation.updatedAt).toLocaleString('pt-BR') })}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {can.edit && (
                    <div className={styles.statusActions}>
                        <button
                            type="button"
                            onClick={() => updateStepStatus(editingItem.id, 'manipulation', 'completed')}
                            className={styles.primaryButton}
                            disabled={isSubmitting}
                        >
                            <FiCheck size={18} /> {t('pages.projectStatus.manipulationStep.complete')}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // --- Renderizar Passo de Controle de Qualidade ---
    const renderQualityStep = () => {
        return (
            <div className={styles.stepContent}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <span className={styles.cardIcon}><FiClipboard size={18} /></span>
                        <h4 className={styles.cardTitle}>{t('pages.projectStatus.qualityStep.title')}</h4>
                    </div>
                    <div className={styles.cardBody}>
                        <div className={styles.formGrid}>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.projectStatus.qualityStep.analysisDate')}</label>
                                <input
                                    type="date"
                                    className={styles.input}
                                    value={editingItem?.steps?.quality?.data?.analysisDate || ''}
                                    onChange={(e) => {
                                        const updatedSteps = {
                                            ...(editingItem?.steps || {}),
                                            quality: {
                                                ...(editingItem?.steps?.quality || {}),
                                                data: {
                                                    ...(editingItem?.steps?.quality?.data || {}),
                                                    analysisDate: e.target.value
                                                }
                                            }
                                        };
                                        setEditingItem(prev => prev ? { ...prev, steps: updatedSteps } : prev);
                                    }}
                                    disabled={!can.edit}
                                />
                            </div>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.projectStatus.qualityStep.analyst')}</label>
                                <input
                                    type="text"
                                    placeholder={t('pages.projectStatus.qualityStep.analystPlaceholder')}
                                    className={styles.input}
                                    value={editingItem?.steps?.quality?.data?.analyst || ''}
                                    onChange={(e) => {
                                        const updatedSteps = {
                                            ...(editingItem?.steps || {}),
                                            quality: {
                                                ...(editingItem?.steps?.quality || {}),
                                                data: {
                                                    ...(editingItem?.steps?.quality?.data || {}),
                                                    analyst: e.target.value
                                                }
                                            }
                                        };
                                        setEditingItem(prev => prev ? { ...prev, steps: updatedSteps } : prev);
                                    }}
                                    disabled={!can.edit}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {renderQualityAnalyses()}

                {can.edit && (
                    <div className={styles.stepActions}>
                        <button
                            type="button"
                            onClick={() => updateStepStatus(editingItem.id, 'quality', 'completed')}
                            className={styles.primaryButton}
                            disabled={isSubmitting}
                        >
                            <FiCheck size={18} /> {t('pages.projectStatus.qualityStep.markAsCompleted')}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // --- Renderizar Passo de Envio para o Cliente ---
    const renderClientStep = () => {
        const isCompleted = editingItem?.steps?.client?.status === 'completed';
        const sendDate = editingItem?.steps?.client?.data?.sendDate || '';
        const transportadora = editingItem?.steps?.client?.data?.transportadora || '';
        const trackingCode = editingItem?.steps?.client?.data?.trackingCode || '';

        const handleSendDateChange = (e) => {
            const updatedSteps = {
                ...(editingItem?.steps || {}),
                client: {
                    ...(editingItem?.steps?.client || {}),
                    data: {
                        ...(editingItem?.steps?.client?.data || {}),
                        sendDate: e.target.value
                    }
                }
            };
            setEditingItem(prev => prev ? { ...prev, steps: updatedSteps } : prev);
        };

        const handleTransportadoraChange = (e) => {
            const updatedSteps = {
                ...(editingItem?.steps || {}),
                client: {
                    ...(editingItem?.steps?.client || {}),
                    data: {
                        ...(editingItem?.steps?.client?.data || {}),
                        transportadora: e.target.value
                    }
                }
            };
            setEditingItem(prev => prev ? { ...prev, steps: updatedSteps } : prev);
        };

        const handleTrackingCodeChange = (e) => {
            const updatedSteps = {
                ...(editingItem?.steps || {}),
                client: {
                    ...(editingItem?.steps?.client || {}),
                    data: {
                        ...(editingItem?.steps?.client?.data || {}),
                        trackingCode: e.target.value
                    }
                }
            };
            setEditingItem(prev => prev ? { ...prev, steps: updatedSteps } : prev);
        };

        const handleMarkAsSent = async () => {
            if (!editingItem) return;

            if (!editingItem?.steps?.client?.data?.sendDate) {
                setFormError(t('pages.projectStatus.clientStep.sendDateRequired'));
                return;
            }

            if (!editingItem?.steps?.client?.data?.transportadora) {
                setFormError(t('pages.projectStatus.clientStep.carrierRequired'));
                return;
            }

            if (!editingItem?.steps?.client?.data?.trackingCode) {
                setFormError(t('pages.projectStatus.clientStep.trackingRequired'));
                return;
            }

            try {
                const updatedSteps = {
                    ...(editingItem.steps || {}),
                    client: {
                        ...(editingItem.steps?.client || {}),
                        status: 'completed',
                        updatedAt: new Date().toISOString(),
                        data: {
                            ...(editingItem.steps?.client?.data || {}),
                            sendDate: editingItem.steps.client.data.sendDate,
                            transportadora: editingItem.steps.client.data.transportadora,
                            trackingCode: editingItem.steps.client.data.trackingCode,
                            sentAt: new Date().toISOString()
                        }
                    }
                };

                await updateDocument(editingItem.id, { steps: updatedSteps });

                // ✅ NOTIFICAR SOBRE ENVIO DA AMOSTRA
                await notifyProjectStatusChange(editingItem, 'client', 'sent', 'sent');

                await logActivity(
                    'sample_sent_to_client',
                    `development-project/${editingItem.id}`,
                    `Amostra do projeto ${editingItem.code} - ${editingItem.projectName} enviada para ${editingItem.clientName} (Transportadora: ${updatedSteps.client.data.transportadora})`
                );

                addToast(t('pages.projectStatus.clientStep.sentSuccess'), 'Success');

                setEditingItem(prev => prev ? { ...prev, steps: updatedSteps } : prev);
                setFormSuccess(t('pages.projectStatus.clientStep.sentSuccess'));
            } catch (error) {
                addToast(`${t('pages.projectStatus.clientStep.sendError')}: ${error.message}`, 'Error');
                setFormError(`${t('pages.projectStatus.clientStep.sendError')}: ${error.message}`);
            }
        };

        return (
            <div className={styles.stepContent}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <span className={styles.cardIcon}><FiClipboard size={18} /></span>
                        <h4 className={styles.cardTitle}>{t('pages.projectStatus.clientStep.title')}</h4>
                    </div>
                    <div className={styles.cardBody}>
                        <div className={styles.formGrid}>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.projectStatus.clientStep.sendDate')}</label>
                                <input
                                    type="date"
                                    className={styles.input}
                                    value={sendDate}
                                    onChange={handleSendDateChange}
                                    disabled={isSubmitting || isCompleted || !can.edit}
                                />
                            </div>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.projectStatus.clientStep.carrier')}</label>
                                <select
                                    className={`${styles.input} ${styles.select}`}
                                    value={transportadora}
                                    onChange={handleTransportadoraChange}
                                    disabled={isSubmitting || isCompleted || !can.edit}
                                >
                                    <option value="">{t('pages.projectStatus.clientStep.carrierPlaceholder')}</option>
                                    <option value="DHL">DHL</option>
                                    <option value="Fedex">Fedex</option>
                                    <option value="UPS">UPS</option>
                                </select>
                            </div>
                            <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                <label>{t('pages.projectStatus.clientStep.trackingCode')}</label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    value={trackingCode}
                                    onChange={handleTrackingCodeChange}
                                    placeholder={t('pages.projectStatus.clientStep.trackingCodePlaceholder')}
                                    disabled={isSubmitting || isCompleted || !can.edit}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {can.edit && !isCompleted && (
                    <div className={styles.stepActions}>
                        <button
                            type="button"
                            onClick={handleMarkAsSent}
                            className={styles.primaryButton}
                            disabled={isSubmitting}
                        >
                            <FiUpload size={18} /> {t('pages.projectStatus.clientStep.markAsSent')}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // --- Renderizar Passo de Status da Amostra ---
    const renderStatusStep = () => {
        const isApproved = editingItem?.steps?.status?.status === 'approved';
        const isRejected = editingItem?.steps?.status?.status === 'rejected';
        const isPending = !isApproved && !isRejected;

        const isProductRegistered = editingItem?.productCreated === true;

        return (
            <div className={styles.stepContent}>
                <div className={styles.statusDisplayContainer}>
                    <div className={styles.statusCard}>
                        <div className={styles.statusIcon}>
                            {isProductRegistered ? '✅' : isApproved ? '⏳' : isRejected ? '❌' : '⏳'}
                        </div>
                        <div className={styles.statusInfo}>
                            <h3 className={styles.statusTitle}>
                                {isProductRegistered ? t('pages.projectStatus.statusStep.registered') :
                                    isApproved ? t('pages.projectStatus.statusStep.approved') :
                                        isRejected ? t('pages.projectStatus.statusStep.rejected') : t('pages.projectStatus.statusStep.pending')}
                            </h3>
                            <p className={styles.statusDescription}>
                                {isProductRegistered && t('pages.projectStatus.statusStep.registeredDescription')}
                                {isApproved && !isProductRegistered && t('pages.projectStatus.statusStep.approvedDescription')}
                                {isRejected && t('pages.projectStatus.statusStep.rejectedDescription')}
                                {isPending && t('pages.projectStatus.statusStep.pendingDescription')}
                            </p>
                        </div>
                    </div>
                </div>

                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <span className={styles.cardIcon}><FiClipboard size={18} /></span>
                        <h4 className={styles.cardTitle}>{t('pages.projectStatus.statusStep.decisionDetails')}</h4>
                    </div>
                    <div className={styles.cardBody}>
                        <div className={styles.formGrid}>
                            <div className={styles.inputGroup}>
                                <label>{t('pages.projectStatus.statusStep.responseDate')}</label>
                                <input type="date" className={styles.input} disabled={!can.edit} />
                            </div>
                            <div className={styles.fullWidth}>
                                <label>{t('pages.projectStatus.statusStep.clientObservations')}</label>
                                <textarea className={styles.textarea} rows={3} placeholder={t('pages.projectStatus.statusStep.clientObservationsPlaceholder')} disabled={!can.edit} />
                            </div>
                        </div>
                    </div>
                </div>

                {can.edit && (
                    <div className={styles.stepActions}>
                        <div className={styles.actionGroup}>
                            <button
                                type="button"
                                onClick={handleApproveSample}
                                className={`${styles.primaryButton} ${styles.successButton}`}
                                disabled={isSubmitting || isProductRegistered}
                            >
                                <FiCheck size={18} /> {t('pages.projectStatus.statusStep.approve')}
                            </button>
                            <button
                                type="button"
                                onClick={handleRejectSample}
                                className={`${styles.primaryButton} ${styles.dangerButton}`}
                                disabled={isSubmitting || isProductRegistered}
                            >
                                <FiX size={18} /> {t('pages.projectStatus.statusStep.reject')}
                            </button>
                        </div>
                        {isApproved && !isProductRegistered && (
                            <div className={styles.infoMessage}>
                                <span className={styles.infoIcon}><FiInfo size={16} /></span>
                                {t('pages.projectStatus.statusStep.approvedInfo')}
                            </div>
                        )}
                        {isProductRegistered && (
                            <div className={styles.successMessage}>
                                <span className={styles.successIcon}><FiCheck size={16} /></span>
                                {t('pages.projectStatus.statusStep.registeredInfo')}
                            </div>
                        )}
                        {isRejected && (
                            <div className={styles.warningMessage}>
                                <span className={styles.warningIcon}><FiAlertCircle size={16} /></span>
                                {t('pages.projectStatus.statusStep.rejectedInfo')}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // --- Renderizar Modal de Importação de MPs ---
    const renderImportMPModal = () => {
        if (!showImportMPModal) return null;

        const filteredImportProducts = (allProducts || []).filter(p =>
            p.id !== editingItem?.id &&
            p.rawMaterials &&
            p.rawMaterials.length > 0 &&
            (
                !importSearchTerm ||
                p.commercialName?.toLowerCase().includes(importSearchTerm.toLowerCase()) ||
                p.code?.toLowerCase().includes(importSearchTerm.toLowerCase())
            )
        );

        return (
            <div className={styles.modalBackdrop}>
                <div className={styles.modalContainer} style={{ maxWidth: '700px' }}>
                    <div className={styles.modalHeader}>
                        <div>
                            <h3 className={styles.modalTitle}>{t('pages.projectStatus.importModal.title')}</h3>
                            <p className={styles.modalSubtitle}>{t('pages.projectStatus.importModal.subtitle')}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                setShowImportMPModal(false);
                                setImportSearchTerm('');
                                setFormError('');
                            }}
                            className={styles.modalCloseButton}
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    {formError && (
                        <div className={`${styles.alert} ${styles.error}`} style={{ marginBottom: '15px' }}>
                            <span className={styles.alertIcon}><FiAlertCircle size={18} /></span>
                            {formError}
                        </div>
                    )}

                    <div className={styles.modalBody}>
                        <div className={styles.inputGroup} style={{ marginBottom: '15px' }}>
                            <label htmlFor="importSearch">{t('pages.projectStatus.importModal.search')}</label>
                            <input
                                id="importSearch"
                                type="text"
                                placeholder={t('pages.projectStatus.importModal.searchPlaceholder')}
                                value={importSearchTerm}
                                onChange={(e) => setImportSearchTerm(e.target.value)}
                                className={styles.input}
                                autoFocus
                            />
                        </div>

                        <div className={styles.tableContainer} style={{ maxHeight: '400px', overflow: 'auto' }}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>{t('pages.projectStatus.importModal.code')}</th>
                                        <th>{t('pages.projectStatus.importModal.commercialName')}</th>
                                        <th style={{ width: '100px', textAlign: 'center' }}>{t('pages.projectStatus.importModal.quantity')}</th>
                                        <th style={{ width: '120px' }}>{t('pages.projectStatus.importModal.action')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredImportProducts.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className={styles.emptyTable}>
                                                {importSearchTerm ? t('pages.projectStatus.importModal.noProducts') : t('pages.projectStatus.importModal.noProductsSearch')}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredImportProducts.map(product => (
                                            <tr key={product.id}>
                                                <td><strong>{product.code}</strong></td>
                                                <td>{product.commercialName}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <span className={styles.badgeCount}>
                                                        {product.rawMaterials.length}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleImportMPs(product)}
                                                        className={`${styles.actionButton} ${styles.importButton}`}
                                                        disabled={!can.edit}
                                                        title={t('pages.projectStatus.importModal.import')}
                                                    >
                                                        <FiUpload size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <button
                            type="button"
                            onClick={() => {
                                setShowImportMPModal(false);
                                setImportSearchTerm('');
                                setFormError('');
                            }}
                            className={styles.secondaryButton}
                        >
                            <FiX size={18} /> {t('pages.projectStatus.importModal.close')}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // --- Renderizar Modal de Registro de Produto ---
    const renderProductRegistrationModal = () => {
        if (!showProductRegistrationModal || !productData) return null;

        const handleCloseModal = () => {
            if (!isCreatingProduct) {
                setShowProductRegistrationModal(false);
                setProductData(null);
                setFormError('');
            }
        };

        const handleProductDataChange = (field, value) => {
            setProductData(prev => ({
                ...prev,
                [field]: value
            }));
        };

        const suggestedPrice = (productData.totalFormulationCost || 0) * (parseFloat(productData.markup) || 1);

        return (
            <div className={styles.modalBackdrop}>
                <div className={styles.modalContainer} style={{ maxWidth: '900px' }}>
                    <div className={styles.modalHeader}>
                        <div>
                            <h3 className={styles.modalTitle}>{t('pages.projectStatus.productRegistration.title')}</h3>
                            <p className={styles.modalSubtitle}>
                                {t('pages.projectStatus.productRegistration.subtitle')}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleCloseModal}
                            className={styles.modalCloseButton}
                            disabled={isCreatingProduct}
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    {formError && (
                        <div className={`${styles.alert} ${styles.error}`} style={{ marginBottom: '15px' }}>
                            <span className={styles.alertIcon}><FiAlertCircle size={18} /></span>
                            {formError}
                        </div>
                    )}

                    <div className={styles.modalBody}>
                        <div className={styles.productInfoCard}>
                            <div className={styles.productInfoGrid}>
                                <div className={styles.productInfoItem}>
                                    <label>{t('pages.projectStatus.productRegistration.projectCode')}</label>
                                    <span className={styles.productInfoValue}>{productData.fromProjectCode}</span>
                                </div>
                                <div className={styles.productInfoItem}>
                                    <label>{t('pages.projectStatus.productRegistration.productName')}</label>
                                    <span className={styles.productInfoValue}>{productData.commercialName}</span>
                                </div>
                                <div className={styles.productInfoItem}>
                                    <label>{t('pages.projectStatus.productRegistration.acronym')}</label>
                                    <span className={styles.productInfoValue}>{productData.sigla || 'N/A'}</span>
                                </div>
                                <div className={styles.productInfoItem}>
                                    <label>{t('pages.projectStatus.productRegistration.suggestedPrice')}</label>
                                    <span className={styles.productInfoValue} style={{ color: '#28a745', fontWeight: 'bold' }}>
                                        R$ {suggestedPrice.toFixed(5)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className={styles.productFormSection}>
                            <div className={styles.formSectionHeader}>
                                <h4>{t('pages.projectStatus.productRegistration.basicInformation')}</h4>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.projectStatus.productRegistration.productCode')}</label>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={productData.code || ''}
                                        onChange={(e) => handleProductDataChange('code', e.target.value.toUpperCase())}
                                        placeholder={t('pages.projectStatus.productRegistration.productCodePlaceholder')}
                                        disabled={isCreatingProduct}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.projectStatus.productRegistration.commercialName')}</label>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={productData.commercialName || ''}
                                        onChange={(e) => handleProductDataChange('commercialName', e.target.value)}
                                        placeholder={t('pages.projectStatus.productRegistration.commercialNamePlaceholder')}
                                        disabled={isCreatingProduct}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.projectStatus.productRegistration.acronymLabel')}</label>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={productData.sigla || ''}
                                        onChange={(e) => handleProductDataChange('sigla', e.target.value.toUpperCase())}
                                        placeholder={t('pages.projectStatus.productRegistration.acronymPlaceholder')}
                                        maxLength={5}
                                        disabled={isCreatingProduct}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.projectStatus.productRegistration.situation')}</label>
                                    <select
                                        className={styles.select}
                                        value={productData.situation || 'Ativo'}
                                        onChange={(e) => handleProductDataChange('situation', e.target.value)}
                                        disabled={isCreatingProduct}
                                    >
                                        <option value="Ativo">Ativo</option>
                                        <option value="Inativo">Inativo</option>
                                    </select>
                                </div>
                            </div>

                            <div className={styles.formSectionHeader} style={{ marginTop: '20px' }}>
                                <h4>{t('pages.projectStatus.productRegistration.physicalCharacteristics')}</h4>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.projectStatus.productRegistration.netWeight')}</label>
                                    <input
                                        type="number"
                                        step="0.001"
                                        className={styles.input}
                                        value={productData.netWeight || ''}
                                        onChange={(e) => handleProductDataChange('netWeight', e.target.value)}
                                        placeholder={t('pages.projectStatus.productRegistration.netWeightPlaceholder')}
                                        disabled={isCreatingProduct}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.projectStatus.productRegistration.grossWeight')}</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={styles.input}
                                        value={productData.grossWeight || ''}
                                        onChange={(e) => handleProductDataChange('grossWeight', e.target.value)}
                                        placeholder={t('pages.projectStatus.productRegistration.grossWeightPlaceholder')}
                                        disabled={isCreatingProduct}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.projectStatus.productRegistration.density')}</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={styles.input}
                                        value={productData.density || ''}
                                        onChange={(e) => handleProductDataChange('density', e.target.value)}
                                        placeholder={t('pages.projectStatus.productRegistration.densityPlaceholder')}
                                        disabled={isCreatingProduct}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label>{t('pages.projectStatus.productRegistration.lossIndex')}</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        className={styles.input}
                                        value={productData.lossIndex || ''}
                                        onChange={(e) => handleProductDataChange('lossIndex', e.target.value)}
                                        placeholder={t('pages.projectStatus.productRegistration.lossIndexPlaceholder')}
                                        disabled={isCreatingProduct}
                                    />
                                </div>
                            </div>

                            <div className={styles.formSectionHeader} style={{ marginTop: '20px' }}>
                                <h4>{t('pages.projectStatus.productRegistration.formulationSummary')}</h4>
                            </div>
                            <div className={styles.rawMaterialsSummary}>
                                <div className={styles.summaryStats}>
                                    <div className={styles.statItem}>
                                        <span className={styles.statLabel}>{t('pages.projectStatus.productRegistration.totalRawMaterials')}</span>
                                        <span className={styles.statValue}>{productData.rawMaterials?.length || 0}</span>
                                    </div>
                                    <div className={styles.statItem}>
                                        <span className={styles.statLabel}>{t('pages.projectStatus.productRegistration.totalCost')}</span>
                                        <span className={styles.statValue}>
                                            R$ {(productData.totalFormulationCost || 0).toFixed(5)}
                                        </span>
                                    </div>
                                    <div className={styles.statItem}>
                                        <span className={styles.statLabel}>{t('pages.projectStatus.productRegistration.markupLabel')}</span>
                                        <span className={styles.statValue}>{productData.markup || '1.00'}</span>
                                    </div>
                                    <div className={styles.statItem}>
                                        <span className={styles.statLabel}>{t('pages.projectStatus.productRegistration.suggestedPriceLabel')}</span>
                                        <span className={styles.statValue} style={{ color: '#28a745', fontWeight: 'bold' }}>
                                            R$ {suggestedPrice.toFixed(5)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.infoAlert}>
                                <span className={styles.infoIcon}><FiInfo size={18} /></span>
                                <div className={styles.infoContent}>
                                    <p>
                                        {t('pages.projectStatus.productRegistration.infoAlert')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <button
                            type="button"
                            onClick={handleCloseModal}
                            className={styles.secondaryButton}
                            disabled={isCreatingProduct}
                        >
                            <FiX size={18} /> {t('pages.projectStatus.productRegistration.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={() => handleCreateProductFromProject(productData)}
                            className={styles.primaryButton}
                            disabled={!productData.code || !productData.commercialName || isCreatingProduct}
                        >
                            {isCreatingProduct ? (
                                <>
                                    <FiRefreshCw size={18} className={styles.buttonSpinner} />
                                    {t('pages.projectStatus.productRegistration.registering')}
                                </>
                            ) : (
                                <><FiSave size={18} /> {t('pages.projectStatus.productRegistration.register')}</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // --- Renderizar Modal Principal ---
    const renderProjectModal = () => {
        if (!isModalOpen) return null;

        return (
            <div className={styles.modalBackdrop}>
                <div className={styles.modalContainer}>
                    <div className={styles.modalHeader}>
                        <div>
                            <h2 className={styles.modalTitle}>{t('pages.projectStatus.manageProject')}</h2>
                            {editingItem && (
                                <p className={styles.modalSubtitle}>
                                    {t('pages.projectStatus.editing', { name: editingItem.projectName, code: editingItem.code })}
                                </p>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                if (isFormDirty() && !window.confirm(t('pages.projectStatus.unsavedChanges'))) {
                                    return;
                                }
                                resetForm();
                                setIsModalOpen(false);
                            }}
                            className={styles.modalCloseButton}
                            disabled={isSubmitting}
                        >
                            <FiX size={24} />
                        </button>
                    </div>

                    {formError && (
                        <div className={`${styles.alert} ${styles.error}`}>
                            <span className={styles.alertIcon}><FiAlertCircle size={18} /></span>
                            {formError}
                        </div>
                    )}

                    {formSuccess && (
                        <div className={`${styles.alert} ${styles.success}`}>
                            <span className={styles.alertIcon}><FiCheck size={18} /></span>
                            {formSuccess}
                        </div>
                    )}

                    <div className={styles.modalSteps}>
                        <ProjectSteps
                            currentStep={currentStep}
                            onStepChange={handleStepChange}
                            project={editingItem}
                            t={t}
                        />
                    </div>

                    <div className={styles.modalBody}>
                        <div className={styles.stepContentContainer}>
                            {renderStepContent()}
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <button
                            type="button"
                            onClick={() => {
                                if (isFormDirty() && !window.confirm(t('pages.projectStatus.unsavedChanges'))) {
                                    return;
                                }
                                resetForm();
                                setIsModalOpen(false);
                            }}
                            className={styles.secondaryButton}
                            disabled={isSubmitting}
                        >
                            <FiX size={18} /> {t('common.cancel')}
                        </button>
                        {can.edit && (
                            <button
                                type="button"
                                onClick={handleSave}
                                className={styles.primaryButton}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <FiRefreshCw size={18} className={styles.buttonSpinner} />
                                        {t('common.saving')}
                                    </>
                                ) : (
                                    <><FiSave size={18} /> {t('common.saveChanges')}</>
                                )}
                            </button>
                        )}
                    </div>

                    {renderImportMPModal()}
                </div>
            </div>
        );
    };

    // --- Filtros e Paginação da Tabela Principal ---
    const filteredProjects = useMemo(() => {
        if (!projects) return [];
        const lowerSearch = searchTerm.toLowerCase();

        return projects.filter(item => {
            const matchesSearch = (
                (item.projectName && item.projectName.toLowerCase().includes(lowerSearch)) ||
                (item.code && item.code.toLowerCase().includes(lowerSearch)) ||
                (item.clientName && item.clientName.toLowerCase().includes(lowerSearch))
            );
            const matchesSituation = !filterSituation || item.situation === filterSituation;

            let matchesStepStatus = true;
            if (filterStepStatus) {
                const steps = item.steps || {};

                if (filterStepStatus.endsWith('_pending')) {
                    const stepId = filterStepStatus.replace('_pending', '');
                    if (stepId === 'product_registered') {
                        matchesStepStatus = !item.productCreated;
                    } else {
                        const status = steps[stepId]?.status || 'pending';
                        matchesStepStatus = status === 'pending';
                    }
                } else {
                    if (filterStepStatus === 'product_registered') {
                        matchesStepStatus = item.productCreated === true;
                    } else {
                        const status = steps[filterStepStatus]?.status || 'pending';
                        matchesStepStatus = status === 'completed' || status === 'approved' || status === 'product_registered';
                    }
                }
            }

            return matchesSearch && matchesSituation && matchesStepStatus;
        }).sort((a, b) => {
            const codeA = a.code || '';
            const codeB = b.code || '';
            return codeA.localeCompare(codeB, undefined, { numeric: true });
        });
    }, [projects, searchTerm, filterSituation, filterStepStatus]);

    // Calculate total pages based on filtered projects
    const totalPages = Math.ceil(filteredProjects.length / ITEMS_PER_PAGE);

    // Ensure currentPage is valid when filteredProjects changes
    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        } else if (totalPages === 0) {
            setCurrentPage(1);
        }
    }, [totalPages, currentPage]);

    // Use useMemo for currentProjects with currentPage dependency
    const currentProjects = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredProjects.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredProjects, currentPage]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    // Skeleton loading
    const TableSkeleton = () => {
        const skeletonRows = Array(ITEMS_PER_PAGE).fill(null);
        return (
            <tbody>
                {skeletonRows.map((_, index) => (
                    <tr key={`skeleton-${index}`} className={styles.skeletonRow}>
                        <td><div className={styles.skeletonCell} style={{ width: '80px', height: '20px' }}></div></td>
                        <td><div className={styles.skeletonCell} style={{ width: '150px', height: '20px' }}></div></td>
                        <td><div className={styles.skeletonCell} style={{ width: '120px', height: '20px' }}></div></td>
                        <td><div className={styles.skeletonCell} style={{ width: '100px', height: '28px' }}></div></td>
                        <td><div className={styles.skeletonCell} style={{ width: '200px', height: '28px' }}></div></td>
                    </tr>
                ))}
            </tbody>
        );
    };

    const showSkeleton = mainLoading && !projects?.length;

    return (
        <div className={styles.pageContainer}>
            {/* Toast Container */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Modals */}
            {renderProjectModal()}
            {renderProductRegistrationModal()}
            {renderManipulationDataModal()}

            {/* Header */}
            <header className={styles.header}>
                <div>
                    <h1 className={styles.headerTitle}>{t('pages.projectStatus.title')}</h1>
                    <p className={styles.headerSubtitle}>
                        {t('pages.projectStatus.subtitle')}
                    </p>
                </div>
                <div className={styles.headerActions}>
                    {projects && projects.length > 0 && can.view && (
                        <button
                            type="button"
                            onClick={handlePrintAllShippingLabels}
                            className={`${styles.primaryButton} ${styles.databaseButton}`}
                            disabled={isSubmitting}
                            title={t('pages.projectStatus.shippingLabel.generateAll')}
                        >
                            <FiDatabase size={18} />
                            {t('pages.projectStatus.shippingLabel.generateAll')}
                        </button>
                    )}
                </div>
            </header>

            {/* Barra de Busca e Filtros */}
            <div className={styles.searchBar}>
                <div className={styles.filterRow}>
                    <div className={styles.inputGroup} style={{ flex: 2 }}>
                        <label>{t('common.search')}</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                placeholder={t('pages.projectStatus.search.placeholder')}
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className={styles.input}
                                style={{ paddingLeft: '36px' }}
                            />
                            <FiSearch size={18} style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: 'var(--text-secondary)'
                            }} />
                        </div>
                    </div>
                    <div className={styles.inputGroup} style={{ flex: 1 }}>
                        <label>{t('pages.projectStatus.filters.situation')}</label>
                        <select
                            value={filterSituation}
                            onChange={(e) => {
                                setFilterSituation(e.target.value);
                                setCurrentPage(1);
                            }}
                            className={styles.select}
                        >
                            <option value="">{t('pages.projectStatus.filters.all')}</option>
                            <option value="Active">{t('pages.development.situation.active')}</option>
                            <option value="On Hold">{t('pages.development.situation.onHold')}</option>
                            <option value="Completed">{t('pages.development.situation.completed')}</option>
                            <option value="Cancelled">{t('pages.development.situation.cancelled')}</option>
                        </select>
                    </div>
                    <div className={styles.inputGroup} style={{ flex: 1.5 }}>
                        <label>{t('pages.projectStatus.filters.projectStatus')}</label>
                        <select
                            value={filterStepStatus}
                            onChange={(e) => {
                                setFilterStepStatus(e.target.value);
                                setCurrentPage(1);
                            }}
                            className={styles.select}
                        >
                            <option value="">{t('pages.projectStatus.filters.allStatus')}</option>
                            <option value="formula">{t('pages.projectStatus.filters.formulaCompleted')}</option>
                            <option value="formula_pending">{t('pages.projectStatus.filters.formulaNotStarted')}</option>
                            <option value="manipulation">{t('pages.projectStatus.filters.manipulationCompleted')}</option>
                            <option value="manipulation_pending">{t('pages.projectStatus.filters.manipulationNotStarted')}</option>
                            <option value="quality">{t('pages.projectStatus.filters.qualityCompleted')}</option>
                            <option value="quality_pending">{t('pages.projectStatus.filters.qualityNotStarted')}</option>
                            <option value="client">{t('pages.projectStatus.filters.clientSent')}</option>
                            <option value="client_pending">{t('pages.projectStatus.filters.clientNotStarted')}</option>
                            <option value="status">{t('pages.projectStatus.filters.sampleApproved')}</option>
                            <option value="status_pending">{t('pages.projectStatus.filters.statusNotStarted')}</option>
                            <option value="product_registered">{t('pages.projectStatus.filters.productRegistered')}</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Tabela de Projetos */}
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ width: '100px' }}>{t('pages.development.table.code')}</th>
                            <th>{t('pages.development.table.projectName')}</th>
                            <th>{t('pages.development.table.client')}</th>
                            <th style={{ width: '120px' }}>{t('pages.development.table.situation')}</th>
                            <th style={{ width: '260px' }}>{t('pages.development.table.actions')}</th>
                        </tr>
                    </thead>
                    {showSkeleton ? (
                        <TableSkeleton />
                    ) : (
                        <tbody>
                            {currentProjects.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className={styles.emptyTable}>
                                        {searchTerm || filterSituation || filterStepStatus
                                            ? t('pages.projectStatus.noProjectsFiltered')
                                            : t('pages.projectStatus.noProjects')}
                                    </td>
                                </tr>
                            ) : (
                                currentProjects.map(project => (
                                    <tr key={project.id} className={styles.tableRow}>
                                        <td><strong>{project.code}</strong></td>
                                        <td>{project.projectName}</td>
                                        <td>{project.clientName}</td>
                                        <td><StatusBadge status={project.situation} t={t} /></td>
                                        <td>
                                            <div className={styles.actionsContainer}>
                                                {can.edit && (
                                                    <button
                                                        onClick={() => handleEditClick(project)}
                                                        className={`${styles.actionButton} ${styles.editButton}`}
                                                        disabled={isSubmitting}
                                                        title={t('pages.projectStatus.manageProject')}
                                                    >
                                                        <FiEdit size={14} />
                                                    </button>
                                                )}
                                                {can.generate && project.rawMaterials?.length > 0 && project.preparationSteps?.length > 0 && (
                                                    <button
                                                        onClick={() => handleGenerateProcessoManipulacao(project)}
                                                        className={`${styles.actionButton} ${styles.manipulationButton}`}
                                                        disabled={isSubmitting}
                                                        title={t('pages.development.actions.generateDocument')}
                                                    >
                                                        <FiFileText size={14} />
                                                    </button>
                                                )}
                                                {!can.edit && !can.generate && (
                                                    <span className={styles.textMuted}>{t('pages.projectStatus.noPermission')}</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    )}
                </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && !showSkeleton && (
                <div className={styles.paginationControls}>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={styles.paginationButton}
                    >
                        <FiArrowLeft size={16} /> {t('pages.projectStatus.pagination.previous')}
                    </button>
                    <span className={styles.paginationInfo}>
                        {t('pages.projectStatus.pagination.page', { current: currentPage, total: totalPages })}
                    </span>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={styles.paginationButton}
                    >
                        {t('pages.projectStatus.pagination.next')} <FiArrowRight size={16} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default ProjectStatusPage;